import { AppSelect } from './ui/AppSelect';
import React, { useState, useEffect, useRef } from "react";
import { Wand2, User, Sparkles, Upload, X, Palette, Trash2, Edit2, Plus, Check, Tag, Loader2, ImagePlus, RefreshCw, PackagePlus, ChevronRight, Download } from "lucide-react";
import { authenticatedFetch } from "../utils/api";
import { API_HOST } from "../utils/constants";
import { runImageTaskToUrl, runTaskToCompletion } from "../services/taskService";
import { STYLE_OPTIONS } from "./storyboard/common/Constants";
import { useTheme } from "../context/ThemeContext";
import { useStyle } from "../context/StyleContext";
import { useToast } from "../context/ToastContext";
import { ConfirmDialog } from '@ui';
import AssetRenameDialog from "./assets/AssetRenameDialog";
import WorkshopVariantUpload from "./assets/WorkshopVariantUpload";
import { createWorkshopAssetSaveQueue, mergeWorkshopAssetRegistration, uploadWorkshopVariant, validateVariantImageFile } from "../utils/workshopVariantUpload";
import WorkshopArtwork from "./ui/WorkshopArtwork";
import "./assets/WorkshopShell.css";
import { ModelArkImageLibraryStatus, ModelArkLibraryToolbar } from './assets/ModelArkLibraryControls';
import { useModelArkAssets } from '../hooks/useModelArkAssets';
import { collectWorkshopModelArkReferences } from '../utils/modelArkAssets';
import { applyAssetCreateResult, applyAssetStatusResult, assetLibraryLabel, assetStatusLabel, clearVideoAssetBinding, getEffectiveAssetMeta, getVideoAssetFieldNames, isAssetReadyStatus } from "../utils/guaVideoAssetStatus";
import { getProjectVideoAssetApiBase, getProjectVideoProvider, isOverseasVideoProvider, supportsVideoAssetIngest } from "../utils/projectRuntime";
import { useWorkshopImageSettings } from "../hooks/useWorkshopImageSettings";
import { getCompatibleImageAspectOptions, normalizeDatastartImageAspectRatio } from '../constants/datastartImageModels';
import {
  WANX_IMAGE_MAX_INPUT_IMAGES,
  assertWanxImageInputLimit,
  isWanxImageProvider,
  normalizeWanxImageQualityTier,
} from "../utils/wanxImageCapabilities";
import RandomCastingWorkspace from "./character/RandomCastingWorkspace";
import { useDelayedUnmount } from "../hooks/useDelayedUnmount";
import Exitable from "./common/Exitable";
import {
  isLatestWorkshopListRequest,
  isSameWorkshopAssetId,
  mergeWorkshopAssetById,
  nextWorkshopListRequestSequence,
} from "../utils/workshopAssetState";

const ADVANCED_CHARACTER_TEMPLATE_TITLE = "高级角色设计版模板";
const DEFAULT_CHARACTER_LAYOUT_MODE = "face_turnaround_sheet";
const CHARACTER_MAIN_REFERENCE_LAYOUTS = [
  {
    id: "face_turnaround_sheet",
    label: "大脸三视图",
    description: "左侧超大脸部特写，右侧无头正面与侧面、背面视图。",
  },
  {
    id: "standard_reference",
    label: "标准定妆照",
    description: "单人主参考图，适合直接给视频模型使用。",
  },
  {
    id: "four_grid_sheet",
    label: "四宫格定妆表",
    description: "脸部四宫格加三视图，用于锁定脸和服装。",
  },
  {
    id: "turnaround_sheet",
    label: "多视角转面图",
    description: "正面、侧面、背面等角度，便于统一造型。",
  },
  {
    id: "raw_prompt",
    label: "无版式",
    description: "只使用描述和项目画风，不额外追加版式。",
  },
  {
    id: "advanced_design_board",
    label: "高级角色设计板",
    description: "中文角色设定板，含多视角、头部研究、服装拆解和制作备注。",
  },
];

const isAdvancedCharacterTemplate = (variation) =>
  (variation?.title || "").includes(ADVANCED_CHARACTER_TEMPLATE_TITLE);

const CHARACTER_CUSTOM_ASPECT_LAYOUTS = new Set(["standard_reference", "raw_prompt"]);
const CHARACTER_ASPECT_RATIO_OPTIONS = [
  { value: "9:16", label: "9:16 竖屏" },
  { value: "3:4", label: "3:4 竖图" },
  { value: "1:1", label: "1:1 方图" },
  { value: "4:3", label: "4:3 横图" },
  { value: "16:9", label: "16:9 横屏" },
];

const supportsCharacterAspectRatio = (layoutMode) => CHARACTER_CUSTOM_ASPECT_LAYOUTS.has(layoutMode);

const buildCharacterLayoutRequestFields = (layoutMode, aspectRatio) => {
  const resolvedLayoutMode = CHARACTER_MAIN_REFERENCE_LAYOUTS.some((option) => option.id === layoutMode)
    ? layoutMode
    : DEFAULT_CHARACTER_LAYOUT_MODE;

  return {
    layoutMode: resolvedLayoutMode,
    ...(supportsCharacterAspectRatio(resolvedLayoutMode) ? { aspectRatio: aspectRatio || "9:16" } : {}),
  };
};

const getCharacterVariantLayoutHint = (layoutMode, aspectRatio) => {
  const option = CHARACTER_MAIN_REFERENCE_LAYOUTS.find((item) => item.id === layoutMode)
    || CHARACTER_MAIN_REFERENCE_LAYOUTS.find((item) => item.id === DEFAULT_CHARACTER_LAYOUT_MODE);
  const ratioHint = supportsCharacterAspectRatio(option.id) ? `，比例 ${aspectRatio || "9:16"}` : "";
  return `输出遵循当前“${option.label}”版式${ratioHint}；仅按变体要求调整服装、配饰或指定细节。`;
};

const CharacterAspectRatioSelector = ({ value, onChange, theme, imageProvider, disabled = false, compact = false }) => (
  <div className="flex flex-wrap gap-1.5">
    {getCompatibleImageAspectOptions(CHARACTER_ASPECT_RATIO_OPTIONS, imageProvider).map((option) => {
      const active = normalizeDatastartImageAspectRatio(value, imageProvider) === option.value;
      return (
        <button
          key={option.value}
          type="button"
          disabled={disabled}
          onClick={() => onChange(option.value)}
          className={`workshop-secondary-action ${compact ? "px-2 py-1 text-2xs" : "px-2.5 py-1.5 text-xs"} rounded-full font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-50`}
          style={{
            backgroundColor: active ? `${theme.colors.accentPrimary}22` : 'var(--color-bg-tertiary)',
            border: `1px solid ${active ? theme.colors.accentPrimary : 'var(--color-border-primary)'}`,
            color: active ? theme.colors.accentPrimary : 'var(--color-text-muted)',
          }}
          aria-pressed={active}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

const buildAdvancedCharacterTemplatePrompt = ({ name, aliases, description }) => {
  const characterName = name || "未命名角色";
  const aliasText = Array.isArray(aliases) && aliases.length > 0 ? aliases.join("、") : "无";
  const characterDescription = description || "以参考角色图中的外貌、服装、体态和气质为准。";

  return `创建一张电影制作级别的高级角色设计版模板，用于导演、选角团队、服装部门和美术部门参考。

角色姓名：${characterName}
角色别名：${aliasText}
角色基础描述：${characterDescription}

核心要求（不可违背）：
不要生成普通模板图。不要使用机械化的平均网格。不要为了对称而对称。
整体构图必须像经过美术指导设计过，具有明确的视觉安排，允许轻微不对称。
每个区域都要像被认真摆放，而不是自动生成。

角色身份：
姓名：${characterName}
别名：${aliasText}
年龄：根据角色描述和参考图合理判断
身高：根据角色描述和参考图合理判断
体型：身体类型、比例、姿态倾向
设计语言：基于角色身份、地域、职业、时代背景形成的视觉语言

面部设计：
脸型结构：脸型、骨相、五官比例
皮肤：肤色、质感、瑕疵、生活痕迹
眼睛：大小、间距、眼神、表现力
头发：发型、质地、动态、自然瑕疵
独特特征：疤痕、痣、酒窝、皱纹、疲态、习惯性表情等

心理画像：
核心性格：3到5个主要性格特征
内在冲突：角色想要什么，以及阻碍他的是什么
行为习惯：3个能暴露性格的习惯动作
情绪基线：默认情绪状态，以及情绪变化速度

表演指导：
角色必须像真实演员被捕捉在某个瞬间，而不是摆拍。
必须有微表情，例如嘴唇紧绷、眼神游移、眉头细微变化。
避免舞台式对称姿态。
捕捉正在变化中的情绪。
身体语言：姿态倾向
运动节奏：僵硬、利落、跳跃、拖拽、迟疑等
静止状态：小动作、紧张、压抑、放松、下意识反应等

服装设计：
服装1：面料、磨损、瑕疵
服装2：版型、变形、缝线
层次逻辑：内外层、季节、职业、经济状态
鞋履：材质、磨损方式
配饰：能体现人物性格的物件
道具：强化角色身份的随身物

材质准确性：
布料必须体现拉伸、缝线、褶皱、磨损。
除非设定需要，不要出现塑料质感。
皮肤必须有柔和光线反应。
加入真实瑕疵，例如灰尘、污渍、老化、使用痕迹。

转面图要求（严格）：
包含全身正面、四分之三侧面、侧面、背面、四分之三背面。
所有角度必须保持完全一致的身体比例、脸部特征、服装结构和细节。
不同角度之间不能漂移，不能重新设计。

头部研究：
正面中性表情
四分之三角度，体现主要性格
侧面，体现骨相结构
低头
仰头
动态强情绪角度
所有表情都要像正在思考或情绪变化中，不要摆拍。

电影感肖像：
环境：与角色身份相关的地点
打光：实际光源、明暗反差
色调：符合剧情和人物气质的色调
表情：具体剧情瞬间
镜头：50mm或85mm镜头感，浅景深，真实感

版式：
干净、有美术指导感的角色设计板。
中性灰背景。
包含身高比例、中文标注、服装拆解、制作备注。
整体必须像高级影视项目开发用的角色设定板。

一致性规则（严格）：
脸、身体比例、服装、发型、配饰和细节在所有视图中必须完全一致。
不同角度之间不能重新解释角色。
必须保持同一个角色。

输出要求：
极高细节。
清晰聚焦。
制作级完成度。
适合影视开发、服装造型、角色统一和项目提案使用。
所有文字、标题、标注、备注必须使用中文。
不要出现英文占位符、乱码、水印、logo。`;
};

const CharacterLayoutModeSelector = ({ value, onChange, theme, compact = false, disabled = false }) => {
  if (compact) {
    return (
      <div className="flex flex-wrap gap-1.5">
        {CHARACTER_MAIN_REFERENCE_LAYOUTS.map((option) => {
          const active = value === option.id;
          return (
            <button
              key={option.id}
              type="button"
              disabled={disabled}
              onClick={() => onChange(option.id)}
              className="workshop-secondary-action rounded-full px-2.5 py-1 text-2xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              style={{
                backgroundColor: active ? `${theme.colors.accentPrimary}22` : 'var(--color-bg-tertiary)',
                border: `1px solid ${active ? theme.colors.accentPrimary : 'var(--color-border-primary)'}`,
                color: active ? theme.colors.accentPrimary : 'var(--color-text-muted)',
              }}
              aria-pressed={active}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-2 lg:grid-cols-3">
      {CHARACTER_MAIN_REFERENCE_LAYOUTS.map((option) => {
        const active = value === option.id;
        return (
          <button
            key={option.id}
            type="button"
            disabled={disabled}
            onClick={() => onChange(option.id)}
            className="workshop-secondary-action min-h-[58px] rounded-lg px-3 py-2 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            style={{
              backgroundColor: active ? `${theme.colors.accentPrimary}18` : 'var(--color-bg-primary)',
              border: `1px solid ${active ? theme.colors.accentPrimary : 'var(--color-border-primary)'}`,
              color: active ? theme.colors.accentPrimary : 'var(--color-text-secondary)',
              boxShadow: 'none',
            }}
            aria-pressed={active}
          >
            <div className="flex items-center gap-2">
              <span
                className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full"
                style={{
                  border: `1px solid ${active ? theme.colors.accentPrimary : 'var(--color-border-secondary)'}`,
                  backgroundColor: active ? theme.colors.accentPrimary : 'transparent',
                  color: 'var(--color-accent-contrast)',
                }}
              >
                {active && <Check size={11} />}
              </span>
              <span className="text-xs font-bold">{option.label}</span>
            </div>
            <div className="mt-1 line-clamp-2 text-[10px] leading-4" style={{ color: active ? theme.colors.accentPrimary : 'var(--color-text-muted)' }}>
              {option.description}
            </div>
          </button>
        );
      })}
    </div>
  );
};

const CharacterGenerator = ({ projectId }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const { currentStyle, setCurrentStyle } = useStyle();
  const toast = useToast();
  const { imageProvider, imageQualityTier, availableImageQualityTierOptions, handleImageQualityTierChange } = useWorkshopImageSettings();
  const isWanxImage = isWanxImageProvider(imageProvider);
  const maxReferenceImages = isWanxImage ? WANX_IMAGE_MAX_INPUT_IMAGES : 5;

  const runWorkshopImageTaskToUrl = (taskType, payload, options = {}) => {
    const imageInputs = [
      payload?.baseImage,
      payload?.baseImageUrl,
      payload?.refImageUrl,
      ...(Array.isArray(payload?.referenceImages) ? payload.referenceImages : []),
    ].filter(Boolean);
    assertWanxImageInputLimit(imageInputs, imageProvider);
    return runImageTaskToUrl(taskType, {
      ...payload,
      imageQualityTier: normalizeWanxImageQualityTier(payload?.imageQualityTier, imageProvider),
    }, {
      ...options,
      provider: imageProvider,
    });
  };
  const [workspaceMode, setWorkspaceMode] = useState("assets");

  // 角色列表和选中状态
  const [characters, setCharacters] = useState([]);
  const [selectedCharId, setSelectedCharId] = useState(null);

  // 新建角色弹窗
  const [isCreating, setIsCreating] = useState(false);
  const [newCharName, setNewCharName] = useState("");
  const [newCharPrompt, setNewCharPrompt] = useState("");
  const [newCharAliases, setNewCharAliases] = useState("");
  const [newCharacterLayoutMode, setNewCharacterLayoutMode] = useState(DEFAULT_CHARACTER_LAYOUT_MODE);
  const [newCharacterAspectRatio, setNewCharacterAspectRatio] = useState("9:16");
  const [characterLayoutModeMap, setCharacterLayoutModeMap] = useState({});
  const [characterAspectRatioMap, setCharacterAspectRatioMap] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [referenceImages, setReferenceImages] = useState([]); // [{url, label}]
  const [uploadingRefIndex, setUploadingRefIndex] = useState(null); // 正在上传的参考图索引

  // 别名编辑
  const [editingAlias, setEditingAlias] = useState(false);
  const [editAliasText, setEditAliasText] = useState("");

  // 描述编辑
  const [editingDescription, setEditingDescription] = useState(false);
  const [editDescriptionText, setEditDescriptionText] = useState("");

  // 按角色 id 追踪 loading 状态，使用 Set 避免切换角色时状态丢失
  const [regeneratingMainIds, setRegeneratingMainIds] = useState(new Set());
  const [replacingMainIds, setReplacingMainIds] = useState(new Set());
  const [generatingVariantIds, setGeneratingVariantIds] = useState(new Set());
  const [generatingAdvancedTemplateIds, setGeneratingAdvancedTemplateIds] = useState(new Set());
  const [regeneratingVariantKeys, setRegeneratingVariantKeys] = useState(new Set()); // Set of "charId-variantIndex"
  const [refiningIds, setRefiningIds] = useState(new Set());
  const [uploadingAssetIds, setUploadingAssetIds] = useState(new Set());
  const [uploadingAllCharacterAssets, setUploadingAllCharacterAssets] = useState(false);
  const [isExpandingNewPrompt, setIsExpandingNewPrompt] = useState(false);
  const [expandingDescriptionIds, setExpandingDescriptionIds] = useState(new Set());

  // 变体生成 — 按角色 id 隔离面板状态
  const [variantPanelMap, setVariantPanelMap] = useState({}); // { [charId]: { show, mode, name, prompt, refImage, refLabel } }

  // 派生当前角色的变体面板状态
  const variantPanelState = variantPanelMap[selectedCharId] || { show: false, mode: "text", name: "", prompt: "", refImage: null, refLabel: "" };
  const showVariantPanel = variantPanelState.show;
  const variantMode = variantPanelState.mode;
  const variantName = variantPanelState.name;
  const variantPrompt = variantPanelState.prompt;
  const variantRefImage = variantPanelState.refImage;
  const variantRefLabel = variantPanelState.refLabel;

  // 更新当前角色的变体面板状态
  const updateVariantPanel = (patch) => {
    setVariantPanelMap(prev => ({
      ...prev,
      [selectedCharId]: { ...prev[selectedCharId] || { show: false, mode: "text", name: "", prompt: "", refImage: null, refLabel: "" }, ...patch },
    }));
  };
  // 清除指定角色的变体面板
  const clearVariantPanel = (charId) => {
    setVariantPanelMap(prev => { const next = { ...prev }; delete next[charId]; return next; });
  };

  const [deleteVariantIndex, setDeleteVariantIndex] = useState(null);

  // 变体展开（手风琴）
  const [expandedVariantIndex, setExpandedVariantIndex] = useState(null);

  // 变体微调 — 按角色 id 隔离
  const [variantRefinePanelMap, setVariantRefinePanelMap] = useState({}); // { [charId]: { show, prompt } }
  const variantRefineState = variantRefinePanelMap[selectedCharId] || { show: false, prompt: "" };
  const showVariantRefinePanel = variantRefineState.show;
  const variantRefinePrompt = variantRefineState.prompt;
  const updateVariantRefinePanel = (patch) => {
    setVariantRefinePanelMap(prev => ({
      ...prev,
      [selectedCharId]: { ...prev[selectedCharId] || { show: false, prompt: "" }, ...patch },
    }));
  };

  const [editingVariantDesc, setEditingVariantDesc] = useState(false);
  const [editVariantDescText, setEditVariantDescText] = useState("");
  const variantReplaceInputRefs = useRef(new Map());

  // 自动高度 textarea refs
  const refinePromptTextareaRef = useRef(null);
  const variantPromptTextareaRef = useRef(null);
  const variantRefinePromptTextareaRef = useRef(null);

  // 微调模式 — 按角色 id 隔离
  const [refinePanelMap, setRefinePanelMap] = useState({}); // { [charId]: { show, prompt } }
  const refineState = refinePanelMap[selectedCharId] || { show: false, prompt: "" };
  const showRefinePanel = refineState.show;
  const refinePrompt = refineState.prompt;
  const updateRefinePanel = (patch) => {
    setRefinePanelMap(prev => ({
      ...prev,
      [selectedCharId]: { ...prev[selectedCharId] || { show: false, prompt: "" }, ...patch },
    }));
  };

  // 详情页参考图编辑
  const [editingDetailRefs, setEditingDetailRefs] = useState(false);
  const [detailRefImages, setDetailRefImages] = useState([null, null, null, null, null]);
  const [uploadingDetailRefIndex, setUploadingDetailRefIndex] = useState(null);
  const detailRefInputRefs = useRef([]);

  // 其他
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [previewImage, setPreviewImageRaw] = useState(null);
  const [lastPreviewImage, setLastPreviewImage] = useState(null); // 出场动画期间保留图片
  const setPreviewImage = (v) => {
    if (v) setLastPreviewImage(v);
    setPreviewImageRaw(v);
  };
  const previewLightbox = useDelayedUnmount(!!previewImage, 150);
  const fileInputRef = useRef(null);
  const variantRefInputRef = useRef(null);
  const replaceMainInputRef = useRef(null);
  const refImageInputRefs = useRef([]);
  const pollingIdsRef = useRef(new Map()); // entityId -> polling token，支持停止轮询
  const pollingTimersRef = useRef(new Map()); // entityId -> timeoutId，卸载/停止时立即取消
  const uploadTokensRef = useRef(new Map()); // uploadKey -> token，支持忽略已停止的异步提交
  const charactersRef = useRef([]); // 异步回调用，始终拿最新角色列表
  const assetSaveQueueRef = useRef(null);
  if (!assetSaveQueueRef.current) assetSaveQueueRef.current = createWorkshopAssetSaveQueue();
  const currentProjectIdRef = useRef(projectId);
  currentProjectIdRef.current = projectId;
  const characterListRequestSequenceRef = useRef(0);
  const API_BASE = API_HOST;
  const assetProvider = getProjectVideoProvider(projectId);
  const assetApiBase = getProjectVideoAssetApiBase(projectId);
  const assetIngestSupported = supportsVideoAssetIngest(assetProvider);
  const modelArkRegistry = useModelArkAssets({ projectId, projectProvider: assetProvider, references: collectWorkshopModelArkReferences(characters) });

  const createCharacterGuaAsset = async ({ url, name, sourceAssetId, sourceDetail }) => {
    return await authenticatedFetch(`${API_BASE}${assetApiBase}/create`, {
      method: "POST",
      body: JSON.stringify({
        projectId: projectId ? Number(projectId) : undefined,
        url,
        name,
        assetType: "Image",
        typeCode: "character_image",
        source: "character",
        sourceAssetId,
        sourceDetail,
      }),
    });
  };

  const createLocalToken = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  const stopAssetPolling = (entityId) => {
    const timeoutId = pollingTimersRef.current.get(entityId);
    if (timeoutId != null) window.clearTimeout(timeoutId);
    pollingTimersRef.current.delete(entityId);
    pollingIdsRef.current.delete(entityId);
  };

  const scheduleAssetPoll = (entityId, token, poll, delayMs) => {
    if (pollingIdsRef.current.get(entityId) !== token) return;
    const previousTimeoutId = pollingTimersRef.current.get(entityId);
    if (previousTimeoutId != null) window.clearTimeout(previousTimeoutId);
    const timeoutId = window.setTimeout(() => {
      pollingTimersRef.current.delete(entityId);
      if (pollingIdsRef.current.get(entityId) === token) poll();
    }, delayMs);
    pollingTimersRef.current.set(entityId, timeoutId);
  };

  const stopAllAssetPolling = () => {
    pollingTimersRef.current.forEach(timeoutId => window.clearTimeout(timeoutId));
    pollingTimersRef.current.clear();
    pollingIdsRef.current.clear();
  };

  const isUploadCurrent = (key, token) => uploadTokensRef.current.get(key) === token;

  const clearUploadingKey = (key) => {
    setUploadingAssetIds(prev => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const allCharacterUploadKey = "all-characters";

  const saveCharacterAsset = async (asset, { shouldApply = () => true, rebase } = {}) => {
    return assetSaveQueueRef.current.run(asset.id ?? "new", async () => {
      const latest = getCharById(asset.id);
      if (!shouldApply()) return latest;
      if (rebase && !latest) throw new Error("原角色已删除或已切换项目，未保存");
      const payload = rebase ? rebase(latest) : asset;
      const saved = await authenticatedFetch(`${API_BASE}/api/characters`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (saved?.id != null && shouldApply()) {
        // A successful save is newer than every list request already in flight.
        characterListRequestSequenceRef.current = nextWorkshopListRequestSequence(characterListRequestSequenceRef.current);
        charactersRef.current = mergeWorkshopAssetById(charactersRef.current, saved);
        setCharacters(prev => mergeWorkshopAssetById(prev, saved));
      }
      return saved;
    });
  };

  // 切换角色时只重置编辑态，面板状态已按角色 id 隔离无需清空
  useEffect(() => {
    setExpandedVariantIndex(null);
    setEditingVariantDesc(false);
    setEditVariantDescText("");
    setEditingAlias(false);
    setEditAliasText("");
    setEditingDescription(false);
    setEditDescriptionText("");
    setEditingDetailRefs(false);
  }, [selectedCharId]);

  useEffect(() => {
    if (projectId) fetchCharacters();
  }, [projectId]);

  useEffect(() => () => {
    stopAllAssetPolling();
    characterListRequestSequenceRef.current = nextWorkshopListRequestSequence(characterListRequestSequenceRef.current);
  }, [projectId, assetProvider]);

  // 保持 ref 同步
  useEffect(() => { charactersRef.current = characters; }, [characters]);

  // 自动调整 textarea 高度
  useEffect(() => {
    const adjustHeight = (ref) => {
      if (ref.current) {
        ref.current.style.height = 'auto';
        ref.current.style.height = ref.current.scrollHeight + 'px';
      }
    };
    if (showRefinePanel) adjustHeight(refinePromptTextareaRef);
    if (showVariantPanel && variantMode === "text") adjustHeight(variantPromptTextareaRef);
    if (showVariantRefinePanel) adjustHeight(variantRefinePromptTextareaRef);
  }, [showRefinePanel, showVariantPanel, variantMode, showVariantRefinePanel, refinePrompt, variantPrompt, variantRefinePrompt]);

  // 根据 id 从 ref 拿最新角色数据（异步回调安全）
  const getCharById = (id) => charactersRef.current.find(c => String(c.id) === String(id));

  const fetchCharacters = async ({ throwOnError = false } = {}) => {
    const requestSequence = nextWorkshopListRequestSequence(characterListRequestSequenceRef.current);
    characterListRequestSequenceRef.current = requestSequence;
    try {
      const data = await authenticatedFetch(`${API_BASE}/api/characters?projectId=${projectId}`);
      const list = data || [];
      if (!isLatestWorkshopListRequest(requestSequence, characterListRequestSequenceRef.current)) {
        return { applied: false, stale: true, list };
      }
      charactersRef.current = list;
      setCharacters(list);
      if (list.length > 0) setSelectedCharId(currentId => currentId ?? list[0].id);
      // 自动对 Processing 状态的资产启动轮询
      list.forEach(c => {
        const hasProcessing = getEffectiveAssetMeta(c, assetProvider).status === "Processing"
          || (c.variations || []).some(v => getEffectiveAssetMeta(v, assetProvider).status === "Processing");
        if (hasProcessing) pollAssetStatus(c);
      });
      return { applied: true, stale: false, list };
    } catch (e) {
      if (isLatestWorkshopListRequest(requestSequence, characterListRequestSequenceRef.current)) {
        console.error(e);
      }
      if (throwOnError) throw e;
      return { applied: false, stale: false, error: e };
    }
  };

  const recoverCharacterImageAfterTaskError = async (characterId, previousImageUrl = null) => {
    const refreshed = await fetchCharacters();
    if (!refreshed?.applied) return false;
    const asset = refreshed.list.find(item => isSameWorkshopAssetId(item.id, characterId));
    const currentUrl = String(asset?.imageUrl || "").trim();
    return Boolean(currentUrl && currentUrl !== String(previousImageUrl || "").trim());
  };

  const selectedChar = characters.find(c => isSameWorkshopAssetId(c.id, selectedCharId));
  const selectedCharacterLayoutMode = characterLayoutModeMap[selectedCharId] || DEFAULT_CHARACTER_LAYOUT_MODE;
  const setSelectedCharacterLayoutMode = (mode) => {
    if (!selectedCharId) return;
    setCharacterLayoutModeMap(prev => ({ ...prev, [selectedCharId]: mode }));
  };
  const selectedCharacterAspectRatio = characterAspectRatioMap[selectedCharId] || "9:16";
  const setSelectedCharacterAspectRatio = (ratio) => {
    if (!selectedCharId) return;
    setCharacterAspectRatioMap(prev => ({ ...prev, [selectedCharId]: ratio }));
  };
  const charAliases = selectedChar ? (() => { try { return JSON.parse(selectedChar.aliases || '[]'); } catch { return []; } })() : [];
  const charVariations = selectedChar?.variations || [];
  const visibleVariantItems = charVariations
    .map((variation, index) => ({ variation, index }));

  // 派生当前角色的 loading 状态
  const isRegeneratingMain = regeneratingMainIds.has(selectedCharId);
  const isReplacingMain = replacingMainIds.has(selectedCharId);
  const isGeneratingVariant = generatingVariantIds.has(selectedCharId);
  const isGeneratingAdvancedTemplate = generatingAdvancedTemplateIds.has(selectedCharId);
  const isRefiningMain = refiningIds.has(selectedCharId);
  const isExpandingDescription = expandingDescriptionIds.has(selectedCharId);
  const getRegeneratingVariantIndex = (idx) => regeneratingVariantKeys.has(`${selectedCharId}-${idx}`);

  // 校验新建角色表单
  const validateNewCharForm = () => {
    if (!newCharName.trim()) {
      toast.warning("请填写角色名称");
      return false;
    }
    if (!newCharAliases.trim()) {
      toast.warning("请填写角色别名");
      return false;
    }
    if (!newCharPrompt.trim()) {
      toast.warning("请填写角色描述");
      return false;
    }
    return true;
  };

  const createCharacterRecord = async ({ name, description, aliases = [], imageUrl = null, refs = [] }) => {
    const aliasArray = Array.isArray(aliases)
      ? aliases.map((item) => String(item || "").trim()).filter(Boolean)
      : String(aliases || "").split(/[,，、]/).map((item) => item.trim()).filter(Boolean);
    return await saveCharacterAsset({
        projectId,
        name,
        description,
        imageUrl,
        aliases: JSON.stringify(aliasArray),
        referenceImages: refs.length > 0 ? JSON.stringify(refs) : null,
        variations: [],
    });
  };

  // 创建无图角色
  const createCharacterDraft = async () => createCharacterRecord({
    name: newCharName,
    description: newCharPrompt,
    aliases: newCharAliases,
    refs: referenceImages,
  });

  const handleCreateRandomCastingPlan = async (constraints) => {
    const { result } = await runTaskToCompletion("CHARACTER_RANDOM_CASTING", {
      ...constraints,
      projectId,
      styleAnchor: currentStyle,
    }, {
      projectId,
      styleAnchor: currentStyle,
      source: "character_random_casting",
      timeoutMs: 15 * 60 * 1000,
    });
    return result?.result || result?.value || result;
  };

  const handleGenerateRandomCharacterPreview = async (plan, options = {}) => {
    const name = String(plan?.name || "").trim();
    const prompt = String(plan?.imagePrompt || plan?.image_prompt || "").trim();
    if (!name || !prompt) throw new Error("选角结果缺少角色名称或生图提示词");

    try {
      const imageUrl = await runWorkshopImageTaskToUrl("GENERATE_CHARACTER", {
        prompt,
        name,
        projectId,
        imageQualityTier,
        layoutMode: options.layoutMode || DEFAULT_CHARACTER_LAYOUT_MODE,
        aspectRatio: options.aspectRatio,
      }, {
        styleAnchor: currentStyle,
        projectId,
        source: "character_random_casting",
      });
      const candidate = {
        ...plan,
        name,
        imagePrompt: prompt,
        imageUrl,
        layoutMode: options.layoutMode || DEFAULT_CHARACTER_LAYOUT_MODE,
        aspectRatio: options.aspectRatio,
      };
      toast.success("候选角色已生成，确认后再加入角色工坊");
      return candidate;
    } catch (error) {
      toast.error("随机角色生图失败: " + error.message);
      throw error;
    }
  };

  const handleAddRandomCharacterToWorkshop = async (candidate) => {
    const name = String(candidate?.name || "").trim();
    const prompt = String(candidate?.imagePrompt || candidate?.image_prompt || "").trim();
    const imageUrl = String(candidate?.imageUrl || "").trim();
    if (!name || !prompt || !imageUrl) throw new Error("候选角色信息不完整，无法加入角色工坊");

    const saved = await createCharacterRecord({
      name,
      description: prompt,
      aliases: candidate?.aliases || [],
      imageUrl,
    });
    setSelectedCharId(saved?.id || null);
    toast.success("已加入角色工坊");
    try {
      await fetchCharacters();
    } catch (error) {
      console.warn("[CharacterRandomCasting] refresh after add failed", error);
    }
    return saved;
  };

  // 上传参考图
  const handleUploadRefImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (referenceImages.length >= maxReferenceImages) {
      toast.warning(`当前图片模型最多支持${maxReferenceImages}张参考图`);
      e.target.value = "";
      return;
    }
    setUploadingRefIndex(referenceImages.length);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const imageUrl = await authenticatedFetch(`${API_BASE}/api/upload/asset`, { method: "POST", body: formData });
      setReferenceImages(prev => [...prev, { url: imageUrl, label: "" }]);
    } catch (err) {
      toast.error("上传失败: " + err.message);
    } finally {
      setUploadingRefIndex(null);
      e.target.value = "";
    }
  };

  // 删除参考图（新建弹窗）
  const handleRemoveRefImage = (index) => {
    setReferenceImages(prev => prev.filter((_, i) => i !== index));
  };

  // 更新参考图标签
  const handleUpdateRefLabel = (index, label) => {
    setReferenceImages(prev => prev.map((ref, i) => i === index ? { ...ref, label } : ref));
  };

  // 详情页参考图上传
  const handleUploadDetailRefImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (detailRefImages.length >= maxReferenceImages) {
      toast.warning(`当前图片模型最多支持${maxReferenceImages}张参考图`);
      e.target.value = "";
      return;
    }
    setUploadingDetailRefIndex(detailRefImages.length);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const imageUrl = await authenticatedFetch(`${API_BASE}/api/upload/asset`, { method: "POST", body: formData });
      setDetailRefImages(prev => [...prev, { url: imageUrl, label: "" }]);
    } catch (err) {
      toast.error("上传失败: " + err.message);
    } finally {
      setUploadingDetailRefIndex(null);
      e.target.value = "";
    }
  };

  // 删除详情页参考图
  const handleRemoveDetailRefImage = (index) => {
    setDetailRefImages(prev => prev.filter((_, i) => i !== index));
  };

  // 更新详情页参考图标签
  const handleUpdateDetailRefLabel = (index, label) => {
    setDetailRefImages(prev => prev.map((ref, i) => i === index ? { ...ref, label } : ref));
  };

  // 开始编辑详情页参考图
  const handleStartEditDetailRefs = () => {
    const savedRefs = (() => { try { return JSON.parse(selectedChar.referenceImages || '[]'); } catch { return []; } })();
    // 兼容旧数据（纯URL字符串）
    const refs = savedRefs.map(r => typeof r === 'string' ? { url: r, label: '' } : r);
    setDetailRefImages(refs);
    setEditingDetailRefs(true);
  };

  // 保存详情页参考图
  const handleSaveDetailRefs = async () => {
    if (!selectedChar) return;
    try {
      await saveCharacterAsset({ ...selectedChar, referenceImages: detailRefImages.length > 0 ? JSON.stringify(detailRefImages) : null });
      await fetchCharacters();
      setEditingDetailRefs(false);
      toast.success("参考图已更新");
    } catch (e) { toast.error("更新失败: " + e.message); }
  };

  // 新建角色 - AI生成并创建
  const handleGenerateMain = async () => {
    if (!validateNewCharForm()) return;
    try {
      assertWanxImageInputLimit(referenceImages, imageProvider);
    } catch (error) {
      toast.warning(error.message);
      return;
    }
    
    // 先创建无图角色
    setIsGenerating(true);
    let newChar;
    try {
      newChar = await createCharacterDraft();
      await fetchCharacters();
      setSelectedCharId(newChar.id);
      setIsCreating(false); // 关闭弹窗
    } catch (e) {
      toast.error("创建角色失败: " + e.message);
      setIsGenerating(false);
      return;
    }
    
    // 开始异步生成（复用重新生成的逻辑）
    const charId = newChar.id;
    const prompt = newCharPrompt;
    setRegeneratingMainIds(prev => new Set(prev).add(charId));
    
    try {
      const newImageUrl = await runWorkshopImageTaskToUrl("GENERATE_CHARACTER", {
        prompt,
        name: newChar.name,
        projectId,
        imageQualityTier,
        layoutMode: newCharacterLayoutMode,
        aspectRatio: supportsCharacterAspectRatio(newCharacterLayoutMode) ? newCharacterAspectRatio : undefined,
        referenceImages: referenceImages.length > 0 ? referenceImages : undefined,
        workshopAssetType: "character",
        workshopAssetId: charId,
        workshopTarget: "main",
      }, { styleAnchor: currentStyle, projectId, source: "character_workshop" });
      const latest = getCharById(charId) || newChar;
      await saveCharacterAsset({ ...clearVideoAssetBinding(latest), imageUrl: newImageUrl });
      await fetchCharacters();
      // 清空表单
      setNewCharName(""); setNewCharPrompt(""); setNewCharAliases("");
      setNewCharacterLayoutMode(DEFAULT_CHARACTER_LAYOUT_MODE);
      setNewCharacterAspectRatio("9:16");
      setReferenceImages([]);
      toast.success("角色生成成功！");
    } catch (e) {
      const recovered = await recoverCharacterImageAfterTaskError(charId);
      if (recovered) toast.warning("图片已生成，前端回执异常，已自动同步");
      else toast.error("生成失败: " + e.message);
    } finally {
      setRegeneratingMainIds(prev => {
        const next = new Set(prev);
        next.delete(charId);
        return next;
      });
      setIsGenerating(false);
    }
  };

  // 新建角色 - 上传主体图并创建
  const handleUploadMain = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // 先校验表单
    if (!validateNewCharForm()) {
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    
    setIsUploading(true);
    
    // 上传图片
    const formData = new FormData();
    formData.append("file", file);
    let imageUrl;
    try {
      imageUrl = await authenticatedFetch(`${API_BASE}/api/upload/asset`, { method: "POST", body: formData });
    } catch (err) { 
      toast.error("上传失败: " + err.message);
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    
    // 创建有图角色
    try {
      const aliasArray = newCharAliases.split(/[,，]/).map(s => s.trim()).filter(Boolean);
      const saved = await saveCharacterAsset({
          projectId,
          name: newCharName,
          description: newCharPrompt,
          imageUrl: imageUrl,
          aliases: JSON.stringify(aliasArray),
          referenceImages: referenceImages.length > 0 ? JSON.stringify(referenceImages) : null,
          variations: [],
      });
      await fetchCharacters();
      setSelectedCharId(saved?.id || null);
      setIsCreating(false);
      // 清空表单
      setNewCharName(""); setNewCharPrompt(""); setNewCharAliases("");
      setNewCharacterLayoutMode(DEFAULT_CHARACTER_LAYOUT_MODE);
      setNewCharacterAspectRatio("9:16");
      setReferenceImages([]);
      toast.success("角色已创建！");
    } catch (e) {
      toast.error("保存失败: " + e.message);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };



  // 一键生成：为所有有描述但没图片的角色批量生图
  const [batchGenerating, setBatchGenerating] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ done: 0, total: 0 });

  const handleBatchGenerate = async () => {
    const pending = characters.filter(c => c.description && c.description !== "角色定妆照" && !c.imageUrl);
    if (pending.length === 0) return toast.info("所有角色都已有图片，无需生成");
    setBatchGenerating(true);
    setBatchProgress({ done: 0, total: pending.length });
    let successCount = 0;
    for (const char of pending) {
      try {
        setRegeneratingMainIds(prev => new Set(prev).add(char.id));
        const charRefImages = (() => { try { const r = JSON.parse(char.referenceImages || '[]'); return r.map(x => typeof x === 'string' ? { url: x, label: '' } : x); } catch { return []; } })();
        const newImageUrl = await runWorkshopImageTaskToUrl("GENERATE_CHARACTER", {
          prompt: char.description,
          name: char.name,
          projectId,
          imageQualityTier,
          layoutMode: DEFAULT_CHARACTER_LAYOUT_MODE,
          referenceImages: charRefImages.length > 0 ? charRefImages : undefined,
          workshopAssetType: "character",
          workshopAssetId: char.id,
          workshopTarget: "main",
        }, { styleAnchor: currentStyle, projectId, source: "character_workshop" });
        const latest = getCharById(char.id) || char;
        await saveCharacterAsset({ ...clearVideoAssetBinding(latest), imageUrl: newImageUrl });
        successCount++;
      } catch (e) {
        const recovered = await recoverCharacterImageAfterTaskError(char.id, char.imageUrl);
        if (recovered) {
          successCount++;
        } else {
          console.error(`[BatchGen] 角色 ${char.name} 生成失败:`, e.message);
        }
        if (!recovered && isWanxImage && String(e.message || "").includes("千问图像3.0最多支持")) {
          toast.error(`${char.name}：${e.message}`);
        }
      } finally {
        setRegeneratingMainIds(prev => { const next = new Set(prev); next.delete(char.id); return next; });
        setBatchProgress(prev => ({ ...prev, done: prev.done + 1 }));
      }
    }
    await fetchCharacters();
    setBatchGenerating(false);
    toast.success(`一键生成完成：${successCount}/${pending.length} 个角色`);
  };

  // 主体图重新生成
  const handleRegenerateMain = async () => {
    if (!selectedChar) return;
    const charId = selectedChar.id;
    const charSnapshot = { ...selectedChar };
    const prompt = charSnapshot.description;
    if (!prompt || prompt === "角色定妆照") return toast.warning("该角色没有描述，无法重新生成");
    setRegeneratingMainIds(prev => new Set(prev).add(charId));
    try {
      const charRefImages = (() => { try { const r = JSON.parse(charSnapshot.referenceImages || '[]'); return r.map(x => typeof x === 'string' ? { url: x, label: '' } : x); } catch { return []; } })();
      const newImageUrl = await runWorkshopImageTaskToUrl("GENERATE_CHARACTER", {
        prompt,
        name: charSnapshot.name,
        projectId,
        imageQualityTier,
        layoutMode: selectedCharacterLayoutMode,
        aspectRatio: supportsCharacterAspectRatio(selectedCharacterLayoutMode) ? selectedCharacterAspectRatio : undefined,
        referenceImages: charRefImages.length > 0 ? charRefImages : undefined,
        workshopAssetType: "character",
        workshopAssetId: charId,
        workshopTarget: "main",
      }, { styleAnchor: currentStyle, projectId, source: "character_workshop" });
      const latest = getCharById(charId) || charSnapshot;
      stopAssetPolling(charId);
      await saveCharacterAsset({ ...clearVideoAssetBinding(latest), imageUrl: newImageUrl });
      await fetchCharacters();
      toast.success("重新生成成功！");
    } catch (e) {
      const recovered = await recoverCharacterImageAfterTaskError(charId, charSnapshot.imageUrl);
      if (recovered) toast.warning("图片已生成，前端回执异常，已自动同步");
      else toast.error("生成失败: " + e.message);
    } finally {
      setRegeneratingMainIds(prev => {
        const next = new Set(prev);
        next.delete(charId);
        return next;
      });
    }
  };

  // 主体图微调（基于当前图 + 修改描述）
  const handleRefineMain = async () => {
    if (!selectedChar || !selectedChar.imageUrl) return;
    if (!refinePrompt.trim()) return toast.warning("请填写修改描述");
    const charId = selectedChar.id;
    const charSnapshot = { ...selectedChar };
    const savedRefinePrompt = refinePrompt;
    setRefiningIds(prev => new Set(prev).add(charId));
    try {
      const newImageUrl = await runWorkshopImageTaskToUrl("GENERATE_CHARACTER_VARIANT", {
        originalPrompt: charSnapshot.description || "",
        baseImageUrl: charSnapshot.imageUrl,
        variantPrompt: savedRefinePrompt,
        name: charSnapshot.name,
        projectId,
        imageQualityTier,
        ...buildCharacterLayoutRequestFields(selectedCharacterLayoutMode, selectedCharacterAspectRatio),
        workshopAssetType: "character",
        workshopAssetId: charId,
        workshopTarget: "main",
      }, { styleAnchor: currentStyle, projectId, source: "character_variant" });
      const latest = getCharById(charId) || charSnapshot;
      stopAssetPolling(charId);
      await saveCharacterAsset({ ...clearVideoAssetBinding(latest), imageUrl: newImageUrl });
      await fetchCharacters();
      toast.success("微调完成！");
    } catch (e) {
      const recovered = await recoverCharacterImageAfterTaskError(charId, charSnapshot.imageUrl);
      if (recovered) toast.warning("图片已生成，前端回执异常，已自动同步");
      else toast.error("微调失败: " + e.message);
    } finally {
      setRefiningIds(prev => {
        const next = new Set(prev);
        next.delete(charId);
        return next;
      });
    }
  };

  // 主体图上传替换
  const handleReplaceMainImage = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedChar) return;
    const charId = selectedChar.id;
    const charSnapshot = { ...selectedChar };
    setReplacingMainIds(prev => new Set(prev).add(charId));
    const formData = new FormData();
    formData.append("file", file);
    try {
      const newImageUrl = await authenticatedFetch(`${API_BASE}/api/upload/asset`, { method: "POST", body: formData });
      const latest = getCharById(charId) || charSnapshot;
      stopAssetPolling(charId);
      await saveCharacterAsset({ ...clearVideoAssetBinding(latest), imageUrl: newImageUrl });
      await fetchCharacters();
      toast.success("图片已替换");
    } catch (err) {
      toast.error("上传失败: " + err.message);
    } finally {
      setReplacingMainIds(prev => {
        const next = new Set(prev);
        next.delete(charId);
        return next;
      });
      if (replaceMainInputRef.current) replaceMainInputRef.current.value = "";
    }
  };

  // 删除变体
  const handleDeleteVariant = async (idx) => {
    if (!selectedChar) return;
    const updatedVariations = charVariations.filter((_, i) => i !== idx);
    try {
      await saveCharacterAsset({ ...selectedChar, variations: updatedVariations });
      await fetchCharacters();
      toast.success(charVariations[idx]?.imageUrl ? "变体已移入回收站，可联系管理员恢复" : "未生成图片的变体草稿已移除");
    } catch (e) { toast.error("删除失败: " + e.message); }
    finally { setDeleteVariantIndex(null); }
  };

  // 重新生成变体
  const handleRegenerateVariant = async (idx) => {
    const variant = charVariations[idx];
    if (!selectedChar || !variant) return;
    if (!variant.description) return toast.warning("该变体没有描述，无法重新生成");
    const charId = selectedChar.id;
    const charSnapshot = { ...selectedChar };
    const variantKey = `${charId}-${idx}`;
    setRegeneratingVariantKeys(prev => new Set(prev).add(variantKey));
    try {
      const newImageUrl = await runWorkshopImageTaskToUrl("GENERATE_CHARACTER_VARIANT", {
        originalPrompt: charSnapshot.description || "",
        baseImageUrl: charSnapshot.imageUrl,
        variantPrompt: variant.description,
        name: variant.title || charSnapshot.name,
        projectId,
        imageQualityTier,
        ...buildCharacterLayoutRequestFields(selectedCharacterLayoutMode, selectedCharacterAspectRatio),
      }, { styleAnchor: currentStyle, projectId, source: "character_variant" });
      const latest = getCharById(charId) || charSnapshot;
      const latestVariations = latest.variations || [];
      const updatedVariations = latestVariations.map((v, i) => i === idx ? { ...clearVideoAssetBinding(v), imageUrl: newImageUrl } : v);
      stopAssetPolling(charId);
      await saveCharacterAsset({ ...latest, variations: updatedVariations });
      await fetchCharacters();
      toast.success("变体已重新生成！");
    } catch (e) { toast.error("生成失败: " + e.message); }
    finally {
      setRegeneratingVariantKeys(prev => {
        const next = new Set(prev);
        next.delete(variantKey);
        return next;
      });
    }
  };

  // 变体微调（基于主体图 + 变体图 + 修改描述）
  const handleRefineVariant = async (idx) => {
    const variant = charVariations[idx];
    if (!selectedChar || !variant?.imageUrl) return;
    if (!variantRefinePrompt.trim()) return toast.warning("请填写修改描述");
    const charId = selectedChar.id;
    const charSnapshot = { ...selectedChar };
    const savedPrompt = variantRefinePrompt;
    const variantKey = `${charId}-${idx}`;
    setRegeneratingVariantKeys(prev => new Set(prev).add(variantKey));
    try {
      const newImageUrl = await runWorkshopImageTaskToUrl("GENERATE_CHARACTER_VARIANT", {
        originalPrompt: charSnapshot.description || "",
        baseImageUrl: variant.imageUrl,
        variantPrompt: savedPrompt,
        name: variant.title || charSnapshot.name,
        projectId,
        imageQualityTier,
        ...buildCharacterLayoutRequestFields(selectedCharacterLayoutMode, selectedCharacterAspectRatio),
      }, { styleAnchor: currentStyle, projectId, source: "character_variant" });
      const latest = getCharById(charId) || charSnapshot;
      const latestVariations = latest.variations || [];
      const updatedVariations = latestVariations.map((v, i) => i === idx ? { ...clearVideoAssetBinding(v), imageUrl: newImageUrl } : v);
      stopAssetPolling(charId);
      await saveCharacterAsset({ ...latest, variations: updatedVariations });
      await fetchCharacters();
      toast.success("变体微调完成！");
    } catch (e) { toast.error("微调失败: " + e.message); }
    finally {
      setRegeneratingVariantKeys(prev => { const next = new Set(prev); next.delete(variantKey); return next; });
    }
  };

  // 直接上传最终变体图片，不调用 AI，也不自动提交厂商素材库。
  const handleUploadNewVariant = async ({ assetId, file, title, description }) => {
    const targetProjectId = projectId;
    const isCurrentProject = () => isSameWorkshopAssetId(currentProjectIdRef.current, targetProjectId);
    const saved = await uploadWorkshopVariant({
      assetId, projectId: targetProjectId, file, title, description,
      uploadFile: async (image) => {
        const body = new FormData();
        body.append("file", image);
        return authenticatedFetch(`${API_BASE}/api/upload/asset`, { method: "POST", body });
      },
      getAsset: (id) => isCurrentProject() ? getCharById(id) : null,
      saveAsset: (asset, rebase) => saveCharacterAsset(asset, { rebase, shouldApply: isCurrentProject }),
      onBeforeSave: () => stopAssetPolling(assetId),
    });
    if (isCurrentProject()) await fetchCharacters();
    return saved;
  };

  // 已有变体可补图或替换图片。
  const handleReplaceVariantImage = async (e, idx) => {
    const input = e.target;
    const file = input.files[0];
    if (!file || !selectedChar) return;
    const charId = selectedChar.id;
    const targetProjectId = projectId;
    const original = charVariations[idx];
    if (!original) return;
    const isCurrentProject = () => isSameWorkshopAssetId(currentProjectIdRef.current, targetProjectId);
    const variantKey = `${charId}-${idx}`;
    setRegeneratingVariantKeys(prev => new Set(prev).add(variantKey));
    const formData = new FormData();
    formData.append("file", file);
    try {
      validateVariantImageFile(file);
      const newImageUrl = await authenticatedFetch(`${API_BASE}/api/upload/asset`, { method: "POST", body: formData });
      const latest = isCurrentProject() ? getCharById(charId) : null;
      if (!latest) throw new Error("原角色已删除或已切换项目，未替换图片");
      const latestVariations = latest.variations || [];
      const current = latestVariations[idx];
      if (!current || ["imageUrl", "title", "description"].some(field => (current[field] || "") !== (original[field] || ""))) {
        throw new Error("变体已发生变化，请重新选择后上传");
      }
      const updatedVariations = latestVariations.map((v, i) => i === idx ? { ...clearVideoAssetBinding(v), imageUrl: newImageUrl } : v);
      stopAssetPolling(charId);
      await saveCharacterAsset({ ...latest, variations: updatedVariations }, {
        shouldApply: isCurrentProject,
        rebase: currentAsset => {
          const currentVariant = currentAsset.variations?.[idx];
          if (!currentVariant || ["imageUrl", "title", "description"].some(field => (currentVariant[field] || "") !== (original[field] || ""))) {
            throw new Error("变体已发生变化，请重新选择后上传");
          }
          return {
            ...currentAsset,
            variations: currentAsset.variations.map((v, i) => i === idx ? { ...clearVideoAssetBinding(v), imageUrl: newImageUrl } : v),
          };
        },
      });
      if (isCurrentProject()) await fetchCharacters();
      toast.success(original.imageUrl ? "变体图片已替换" : "变体图片已上传");
    } catch (err) { toast.error("上传失败: " + err.message); }
    finally {
      setRegeneratingVariantKeys(prev => { const next = new Set(prev); next.delete(variantKey); return next; });
      if (input) input.value = "";
    }
  };

  // 变体单独入库
  const handleUploadVariantToAssets = async (idx) => {
    if (!assetIngestSupported) return toast.info("当前项目视频服务不支持素材入库");
    const variant = charVariations[idx];
    if (!selectedChar || !variant?.imageUrl) return toast.warning("没有变体图，无法入库");
    if (!projectId) return toast.warning("缺少项目ID，无法入库");
    const charId = selectedChar.id;
    const uploadKey = `${charId}-v${idx}`;
    const token = createLocalToken();
    uploadTokensRef.current.set(uploadKey, token);
    setUploadingAssetIds(prev => new Set(prev).add(uploadKey));
    try {
      const res = await createCharacterGuaAsset({
        url: variant.imageUrl,
        name: `${selectedChar.name || "角色"}-${variant.title || `变体${idx + 1}`}`,
        sourceAssetId: charId,
        sourceDetail: "character_workshop_variant",
      });
      if (!isUploadCurrent(uploadKey, token)) return;
      const applied = applyAssetCreateResult(variant, res, assetProvider);
      if (applied.accepted) {
        const updatedVariations = charVariations.map((v, i) => i === idx ? applied.target : v);
        const updated = { ...selectedChar, variations: updatedVariations };
        await saveCharacterAsset(updated, {
          shouldApply: () => isUploadCurrent(uploadKey, token),
          rebase: latest => mergeWorkshopAssetRegistration(latest, updated, getVideoAssetFieldNames(assetProvider)),
        });
        if (!isUploadCurrent(uploadKey, token)) return;
        await fetchCharacters();
        pollAssetStatus(updated);
        toast.success(`${assetLibraryLabel(applied.provider)}变体入库已提交`);
      }
    } catch (e) { toast.error("入库失败: " + e.message); }
    finally {
      if (isUploadCurrent(uploadKey, token)) {
        uploadTokensRef.current.delete(uploadKey);
        clearUploadingKey(uploadKey);
      }
    }
  };

  // 保存变体描述
  const handleSaveVariantDescription = async (idx) => {
    if (!selectedChar) return;
    try {
      const updatedVariations = charVariations.map((v, i) => i === idx ? { ...v, description: editVariantDescText } : v);
      await saveCharacterAsset({ ...selectedChar, variations: updatedVariations });
      await fetchCharacters();
      setEditingVariantDesc(false);
      toast.success("变体描述已更新");
    } catch (e) { toast.error("更新失败: " + e.message); }
  };
  const handleSaveAlias = async () => {
    if (!selectedChar) return;
    try {
      const aliasArray = editAliasText.split(/[,，]/).map(s => s.trim()).filter(Boolean);
      await saveCharacterAsset({ ...selectedChar, aliases: JSON.stringify(aliasArray) });
      await fetchCharacters();
      setEditingAlias(false);
      toast.success("别名已更新");
    } catch (e) { toast.error("更新失败: " + e.message); }
  };

  // 保存描述
  const handleSaveDescription = async () => {
    if (!selectedChar) return;
    try {
      await saveCharacterAsset({ ...selectedChar, description: editDescriptionText });
      await fetchCharacters();
      setEditingDescription(false);
      toast.success("描述已更新");
    } catch (e) { toast.error("更新失败: " + e.message); }
  };

  const requestExpandedCharacterPrompt = async ({ name, aliases, description }) => {
    const { result } = await runTaskToCompletion("CHARACTER_EXPAND_PROMPT", {
        name,
        aliases: Array.isArray(aliases) ? aliases.join("，") : aliases,
        description,
        styleAnchor: currentStyle,
        projectId,
      }, {
        projectId,
        source: "character_expand_prompt",
        styleAnchor: currentStyle,
        timeoutMs: 15 * 60 * 1000,
    });
    return result?.result || result?.value || result;
  };

  const handleExpandNewPrompt = async () => {
    if (!newCharPrompt.trim()) {
      toast.warning("请先写一点性格或粗略描述");
      return;
    }
    setIsExpandingNewPrompt(true);
    try {
      const data = await requestExpandedCharacterPrompt({
        name: newCharName,
        aliases: newCharAliases,
        description: newCharPrompt,
      });
      setNewCharPrompt(data.expandedDescription || data.image_prompt || data.description || newCharPrompt);
      toast.success("已扩写成人物生成提示词");
    } catch (e) {
      toast.error(e.message || "扩写失败");
    } finally {
      setIsExpandingNewPrompt(false);
    }
  };

  const handleExpandDescription = async () => {
    if (!selectedChar) return;
    const sourceDescription = editingDescription ? editDescriptionText : (selectedChar.description || "");
    if (!sourceDescription.trim()) {
      toast.warning("请先写一点性格或粗略描述");
      return;
    }

    setExpandingDescriptionIds(prev => new Set(prev).add(selectedChar.id));
    try {
      const data = await requestExpandedCharacterPrompt({
        name: selectedChar.name,
        aliases: charAliases,
        description: sourceDescription,
      });
      const expandedDescription = data.expandedDescription || data.image_prompt || data.description || sourceDescription;

      if (editingDescription) {
        setEditDescriptionText(expandedDescription);
      } else {
        await saveCharacterAsset({ ...selectedChar, description: expandedDescription });
        await fetchCharacters();
      }
      toast.success("已按五官体态规则扩写");
    } catch (e) {
      toast.error(e.message || "扩写失败");
    } finally {
      setExpandingDescriptionIds(prev => {
        const next = new Set(prev);
        next.delete(selectedChar.id);
        return next;
      });
    }
  };

  // 上传变体参考图
  const handleUploadVariantRef = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const imageUrl = await authenticatedFetch(`${API_BASE}/api/upload/asset`, { method: "POST", body: formData });
      updateVariantPanel({ refImage: imageUrl });
    } catch (err) { toast.error("上传失败: " + err.message); }
    finally { if (variantRefInputRef.current) variantRefInputRef.current.value = ""; }
  };

  // 上传素材到 GuaVideo 素材库（主体图 + 所有变体），保存 assetId 和状态
  const handleUploadToAssets = async () => {
    if (!assetIngestSupported) return toast.info("当前项目视频服务不支持素材入库");
    if (!selectedChar?.imageUrl) return toast.warning("没有主体图，无法上传素材库");
    if (!projectId) return toast.warning("缺少项目ID，无法入库");
    const charId = selectedChar.id;
    const uploadKey = charId;
    const token = createLocalToken();
    uploadTokensRef.current.set(uploadKey, token);
    setUploadingAssetIds(prev => new Set(prev).add(charId));
    try {
      const updated = { ...selectedChar };
      // 上传主体图
      try {
        const res = await createCharacterGuaAsset({
          url: updated.imageUrl,
          name: updated.name || "角色主体",
          sourceAssetId: charId,
          sourceDetail: "character_workshop_main",
        });
        if (!isUploadCurrent(uploadKey, token)) return;
        const applied = applyAssetCreateResult(updated, res, assetProvider);
        if (applied.accepted) {
          Object.assign(updated, applied.target);
        }
      } catch (e) { toast.error("主体图入库失败: " + e.message); }

      // 上传变体
      const variations = [...(updated.variations || [])];
      for (let i = 0; i < variations.length; i++) {
        const v = variations[i];
        if (!v.imageUrl) continue;
        try {
          const res = await createCharacterGuaAsset({
            url: v.imageUrl,
            name: `${updated.name || "角色"}-${v.title || `变体${i + 1}`}`,
            sourceAssetId: charId,
            sourceDetail: "character_workshop_variant",
          });
          if (!isUploadCurrent(uploadKey, token)) return;
          const applied = applyAssetCreateResult(v, res, assetProvider);
          if (applied.accepted) {
            variations[i] = applied.target;
          }
        } catch { /* skip */ }
      }
      updated.variations = variations;

      // 保存到后端
      await saveCharacterAsset(updated, {
        shouldApply: () => isUploadCurrent(uploadKey, token),
        rebase: latest => mergeWorkshopAssetRegistration(latest, updated, getVideoAssetFieldNames(assetProvider)),
      });
      if (!isUploadCurrent(uploadKey, token)) return;
      await fetchCharacters();
      const meta = getEffectiveAssetMeta(updated, assetProvider);
      toast.success(`${assetLibraryLabel(meta.provider)}入库已提交，等待处理`);

      // 轮询状态
      pollAssetStatus(updated);
    } catch (e) { toast.error("入库失败: " + e.message); }
    finally {
      if (isUploadCurrent(uploadKey, token)) {
        uploadTokensRef.current.delete(uploadKey);
        clearUploadingKey(uploadKey);
      }
    }
  };

  const handleUploadAllCharactersToAssets = async () => {
    if (!assetIngestSupported) return toast.info("当前项目视频服务不支持素材入库");
    if (!projectId) return toast.warning("缺少项目ID，无法入库");
    const candidates = characters.filter((char) => char.imageUrl || (char.variations || []).some((v) => v.imageUrl));
    if (!candidates.length) return toast.warning("没有可入库的角色图片");

    const token = createLocalToken();
    uploadTokensRef.current.set(allCharacterUploadKey, token);
    setUploadingAllCharacterAssets(true);
    let success = 0;
    let failed = 0;
    const updatedEntities = [];
    try {
      for (const char of candidates) {
        if (!isUploadCurrent(allCharacterUploadKey, token)) return;
        const updated = { ...char };
        if (updated.imageUrl) {
          try {
            const res = await createCharacterGuaAsset({
              url: updated.imageUrl,
              name: updated.name || "角色主体",
              sourceAssetId: char.id,
              sourceDetail: "character_workshop_main",
            });
            if (!isUploadCurrent(allCharacterUploadKey, token)) return;
            const applied = applyAssetCreateResult(updated, res, assetProvider);
            if (applied.accepted) {
              Object.assign(updated, applied.target);
              success += 1;
            }
          } catch {
            failed += 1;
          }
        }

        const variations = [...(updated.variations || [])];
        for (let i = 0; i < variations.length; i += 1) {
          const variant = variations[i];
          if (!variant?.imageUrl) continue;
          try {
            const res = await createCharacterGuaAsset({
              url: variant.imageUrl,
              name: `${updated.name || "角色"}-${variant.title || `变体${i + 1}`}`,
              sourceAssetId: char.id,
              sourceDetail: "character_workshop_variant",
            });
            if (!isUploadCurrent(allCharacterUploadKey, token)) return;
            const applied = applyAssetCreateResult(variant, res, assetProvider);
            if (applied.accepted) {
              variations[i] = applied.target;
              success += 1;
            }
          } catch {
            failed += 1;
          }
        }
        updated.variations = variations;
        updatedEntities.push(updated);
        await saveCharacterAsset(updated, {
          shouldApply: () => isUploadCurrent(allCharacterUploadKey, token),
          rebase: latest => mergeWorkshopAssetRegistration(latest, updated, getVideoAssetFieldNames(assetProvider)),
        });
        if (!isUploadCurrent(allCharacterUploadKey, token)) return;
      }
      await fetchCharacters();
      if (!isUploadCurrent(allCharacterUploadKey, token)) return;
      updatedEntities.forEach((entity) => pollAssetStatus(entity));
      if (failed > 0) toast.warning(`角色图片入库完成 ${success} 张，失败 ${failed} 张`);
      else toast.success(`已提交 ${success} 张角色图片入库`);
    } catch (e) {
      toast.error("批量入库失败: " + e.message);
    } finally {
      if (isUploadCurrent(allCharacterUploadKey, token)) {
        uploadTokensRef.current.delete(allCharacterUploadKey);
        setUploadingAllCharacterAssets(false);
      }
    }
  };

  const handleStopAllCharactersUpload = () => {
    uploadTokensRef.current.delete(allCharacterUploadKey);
    setUploadingAllCharacterAssets(false);
    toast.success("已停止批量入库提交，可以重新入库");
  };

  // 轮询素材状态直到全部 Active 或 Failed
  const pollAssetStatus = (entity) => {
    if (!assetIngestSupported) return;
    const entityId = entity.id;
    if (pollingIdsRef.current.has(entityId)) return; // 防止重复轮询
    const token = createLocalToken();
    pollingIdsRef.current.set(entityId, token);

    const ids = [];
    const mainMeta = getEffectiveAssetMeta(entity, assetProvider);
    const mainAssetId = mainMeta.assetId;
    const mainStatus = mainMeta.status;
    if (mainAssetId && mainStatus === "Processing") {
      ids.push({ assetId: mainAssetId, provider: mainMeta.provider, target: "main", url: entity.imageUrl });
    }
    (entity.variations || []).forEach((v, i) => {
      const meta = getEffectiveAssetMeta(v, assetProvider);
      const assetId = meta.assetId;
      const status = meta.status;
      if (assetId && status === "Processing") {
        ids.push({ assetId, provider: meta.provider, target: "variant", index: i, url: v.imageUrl });
      }
    });
    if (ids.length === 0) { stopAssetPolling(entityId); return; }

    const poll = async () => {
      if (pollingIdsRef.current.get(entityId) !== token) return;
      let allDone = true;
      const updated = { ...entity };
      const variations = [...(updated.variations || [])];

      for (const item of ids) {
        if (pollingIdsRef.current.get(entityId) !== token) return;
        try {
          const res = await authenticatedFetch(`${API_BASE}${assetApiBase}/status`, {
            method: "POST",
            body: JSON.stringify({
              projectId: projectId ? Number(projectId) : undefined,
              url: item.url,
              assetId: item.assetId,
            }),
          });
          if (pollingIdsRef.current.get(entityId) !== token) return;
          const currentTarget = item.target === "main" ? updated : variations[item.index];
          const applied = applyAssetStatusResult(currentTarget, res, assetProvider);
          if (!applied.accepted) {
            allDone = false;
            continue;
          }
          const status = applied.status;
          if (item.target === "main") {
            Object.assign(updated, applied.target);
          } else {
            variations[item.index] = applied.target;
          }
          if (status === "Processing") allDone = false;
        } catch { allDone = false; }
      }
      updated.variations = variations;

      try {
        if (pollingIdsRef.current.get(entityId) !== token) return;
        await saveCharacterAsset(updated, {
          shouldApply: () => pollingIdsRef.current.get(entityId) === token,
          rebase: latest => mergeWorkshopAssetRegistration(latest, updated, getVideoAssetFieldNames(assetProvider)),
        });
        if (pollingIdsRef.current.get(entityId) !== token) return;
      } catch { /* ignore */ }

      if (!allDone) {
        scheduleAssetPoll(
          entityId,
          token,
          poll,
          3000,
        );
      } else {
        if (pollingIdsRef.current.get(entityId) === token) {
          stopAssetPolling(entityId);
        }
        // 最终刷新一次拿最新数据
        fetchCharacters();
      }
    };
    scheduleAssetPoll(
      entityId,
      token,
      poll,
      3000,
    );
  };

  const handleStopAssetUpload = async ({ variantIndex = null, clearCharacterProcessing = false } = {}) => {
    if (!selectedChar) return;
    const entity = getCharById(selectedChar.id) || selectedChar;
    const entityId = entity.id;
    const updated = { ...entity };
    const variations = [...(updated.variations || [])];

    stopAssetPolling(entityId);

    if (variantIndex == null) {
      uploadTokensRef.current.delete(entityId);
      clearUploadingKey(entityId);
      Object.assign(updated, clearVideoAssetBinding(updated));

      if (clearCharacterProcessing) {
        variations.forEach((variant, index) => {
          const key = `${entityId}-v${index}`;
          if (getEffectiveAssetMeta(variant, assetProvider).status === "Processing" || uploadingAssetIds.has(key)) {
            uploadTokensRef.current.delete(key);
            clearUploadingKey(key);
            variations[index] = clearVideoAssetBinding(variant);
          }
        });
      }
    } else {
      const key = `${entityId}-v${variantIndex}`;
      uploadTokensRef.current.delete(key);
      clearUploadingKey(key);
      if (variations[variantIndex]) {
        variations[variantIndex] = clearVideoAssetBinding(variations[variantIndex]);
      }
    }

    updated.variations = variations;
    setCharacters(prev => prev.map(c => c.id === entityId ? updated : c));

    try {
      await saveCharacterAsset(updated);
      const stillProcessing = getEffectiveAssetMeta(updated, assetProvider).status === "Processing"
        || variations.some(v => getEffectiveAssetMeta(v, assetProvider).status === "Processing");
      if (stillProcessing) pollAssetStatus(updated);
      toast.success("已停止入库状态，可以重新入库");
    } catch (e) {
      toast.error("停止失败: " + e.message);
    }
  };

  // 生成变体
  const handleGenerateVariant = async () => {
    if (!selectedChar) return;
    if (!variantName.trim()) return toast.warning("请填写变体名称");
    if (variantMode === "text" && !variantPrompt.trim()) return toast.warning("请填写换装描述");
    if (variantMode === "image" && !variantRefImage) return toast.warning("请上传参考图");
    const charId = selectedChar.id;
    const charSnapshot = { ...selectedChar };
    const savedVariantName = variantName.trim();
    const savedVariantPrompt = variantPrompt;
    const savedVariantMode = variantMode;
    const savedVariantRefImage = variantRefImage;
    const savedVariantRefLabel = variantRefLabel;
    setGeneratingVariantIds(prev => new Set(prev).add(charId));
    try {
      const newImageUrl = await runWorkshopImageTaskToUrl("GENERATE_CHARACTER_VARIANT", {
        originalPrompt: charSnapshot.description || "",
        baseImageUrl: charSnapshot.imageUrl,
        variantPrompt: savedVariantMode === "text" ? savedVariantPrompt : (savedVariantRefLabel || "换装，参考服装图片"),
        ...(savedVariantMode === "image" && { refImageUrl: savedVariantRefImage }),
        name: savedVariantName,
        projectId,
        imageQualityTier,
        ...buildCharacterLayoutRequestFields(selectedCharacterLayoutMode, selectedCharacterAspectRatio),
      }, { styleAnchor: currentStyle, projectId, source: "character_variant" });

      const latest = getCharById(charId) || charSnapshot;
      const latestVariations = latest.variations || [];
      const updatedVariations = [...latestVariations, {
        title: savedVariantName,
        imageUrl: newImageUrl,
        description: savedVariantMode === "text" ? savedVariantPrompt : (savedVariantRefLabel || "参考图换装"),
      }];
      await saveCharacterAsset({ ...latest, variations: updatedVariations });

      await fetchCharacters();
      clearVariantPanel(charId);
      toast.success("变体生成成功！");
    } catch (e) { toast.error("生成失败: " + e.message); }
    finally {
      setGeneratingVariantIds(prev => {
        const next = new Set(prev);
        next.delete(charId);
        return next;
      });
    }
  };

  // 生成高级角色设计版模板：保存为特殊角色变体，供小故事板/GuaVideo 直接选用
  const handleGenerateAdvancedTemplate = async () => {
    if (!selectedChar) return;
    if (!selectedChar.imageUrl) return toast.warning("请先生成或上传角色主体图");

    const charId = selectedChar.id;
    const charSnapshot = { ...selectedChar };
    const templatePrompt = buildAdvancedCharacterTemplatePrompt({
      name: charSnapshot.name,
      aliases: charAliases,
      description: charSnapshot.description || "",
    });

    setGeneratingAdvancedTemplateIds(prev => new Set(prev).add(charId));
    try {
      const newImageUrl = await runWorkshopImageTaskToUrl("GENERATE_CHARACTER_ADVANCED_TEMPLATE", {
        originalPrompt: charSnapshot.description || "",
        baseImageUrl: charSnapshot.imageUrl,
        templatePrompt,
        name: charSnapshot.name,
        projectId,
        imageQualityTier,
      }, { styleAnchor: currentStyle, projectId, source: "character_advanced_template" });

      const latest = getCharById(charId) || charSnapshot;
      const latestVariations = latest.variations || [];
      const existingIndex = latestVariations.findIndex(isAdvancedCharacterTemplate);
      const nextTemplate = {
        title: ADVANCED_CHARACTER_TEMPLATE_TITLE,
        imageUrl: newImageUrl,
        description: templatePrompt,
      };
      const updatedVariations = existingIndex >= 0
        ? latestVariations.map((variation, index) => index === existingIndex ? { ...clearVideoAssetBinding(variation), ...nextTemplate } : variation)
        : [...latestVariations, nextTemplate];

      stopAssetPolling(charId);
      await saveCharacterAsset({ ...latest, variations: updatedVariations });
      await fetchCharacters();
      toast.success("高级角色设计版模板已生成");
    } catch (e) {
      toast.error("高级模板生成失败: " + e.message);
    } finally {
      setGeneratingAdvancedTemplateIds(prev => {
        const next = new Set(prev);
        next.delete(charId);
        return next;
      });
    }
  };

  // 删除角色
  const handleDelete = async () => {
    if (!deleteTargetId) return;
    try {
      await authenticatedFetch(`${API_BASE}/api/characters/${deleteTargetId}`, { method: "DELETE" });
      setCharacters(prev => prev.filter(c => c.id !== deleteTargetId));
      if (selectedCharId === deleteTargetId) setSelectedCharId(null);
      toast.success("已移入回收站，可联系管理员恢复");
    } catch (e) { toast.error("删除失败: " + e.message); } finally { setDeleteTargetId(null); }
  };

  const handleRename = async (name) => {
    if (!renameTarget?.id) return;
    const id = renameTarget.id;
    setRenamingId(id);
    try {
      const updated = await authenticatedFetch(`${API_BASE}/api/characters/${id}/name`, {
        method: "PATCH",
        body: JSON.stringify({ name }),
      });
      setCharacters(prev => prev.map(c => c.id === id ? updated : c));
      toast.success("角色已更名");
      setRenameTarget(null);
    } catch (e) {
      toast.error("更名失败: " + e.message);
    } finally {
      setRenamingId(null);
    }
  };

  return (
    <div className="workshop-shell w-full h-full flex flex-col font-sans" style={{ color: 'var(--color-text-primary)', backgroundColor: 'var(--color-bg-primary)' }}>

      {/* 大图预览 */}
      {previewLightbox.mounted && (previewImage || lastPreviewImage) && (
        <div className={`fixed inset-0 z-[100] flex items-center justify-center p-8 ${previewLightbox.exiting ? 'animate-out fade-out duration-150' : 'animate-in fade-in duration-150'}`} onClick={() => setPreviewImage(null)} style={{ backgroundColor: 'var(--overlay-heavy)', backdropFilter: 'blur(12px)' }}>
          <button className="absolute top-6 right-6 p-2 rounded-full" style={{ backgroundColor: 'var(--color-bg-secondary)', color: 'var(--color-text-primary)' }}><X size={24} /></button>
          <img src={previewImage || lastPreviewImage} className={`max-w-full max-h-full object-contain rounded-xl ${previewLightbox.exiting ? 'animate-out fade-out zoom-out-95 duration-150' : 'animate-in fade-in zoom-in-95 duration-200'}`} onClick={e => e.stopPropagation()} />
        </div>
      )}

      {/* 顶部栏 */}
      <div className="workshop-header h-14 border-b flex items-center justify-between px-6 shrink-0" style={{ borderColor: 'var(--color-border-primary)' }}>
        <div className="workshop-header-heading flex min-w-0 items-center gap-4">
          <h2 className="text-base font-bold flex shrink-0 items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
            <span className="workshop-title-icon p-1.5 rounded-lg"><WorkshopArtwork kind="character" size={16} /></span>
            角色工坊
          </h2>
          <div className="flex rounded-lg p-1" style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)' }}>
            {[
              { id: 'assets', label: '角色资产', icon: User },
              { id: 'random_casting', label: '随机选角', icon: Sparkles },
            ].map((item) => {
              const Icon = item.icon;
              const active = workspaceMode === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setWorkspaceMode(item.id)}
                  className="workshop-secondary-action flex items-center gap-1.5 rounded-md px-3 py-1 text-xs font-bold transition-colors"
                  style={{
                    color: active ? 'white' : 'var(--color-text-muted)',
                    backgroundColor: active ? theme.colors.accentPrimary : 'transparent',
                  }}
                  aria-pressed={active}
                >
                  <Icon size={12} />
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="workshop-toolbar flex items-center gap-2">
          <ModelArkLibraryToolbar registry={modelArkRegistry} />
          <AppSelect
            value={imageQualityTier}
            onChange={(e) => handleImageQualityTierChange(e.target.value)}
            className="px-2.5 py-1.5 rounded-lg text-xs font-bold outline-none"
            title="图片清晰度档位"
            style={{ backgroundColor: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border-primary)' }}
          >
            {availableImageQualityTierOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </AppSelect>
          {workspaceMode === 'assets' && characters.filter(c => c.description && c.description !== "角色定妆照" && !c.imageUrl).length > 0 && (
            <button
              onClick={handleBatchGenerate}
              disabled={batchGenerating}
              className="workshop-primary-action flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold text-xs"
              style={{ background: batchGenerating ? 'var(--color-bg-tertiary)' : 'var(--color-accent-primary)', color: 'white', opacity: batchGenerating ? 0.7 : 1 }}
            >
              {batchGenerating ? <><Loader2 size={13} className="animate-spin" /> {batchProgress.done}/{batchProgress.total}</> : <><Wand2 size={13} /> 一键生成</>}
            </button>
          )}
          {workspaceMode === 'assets' && assetIngestSupported && characters.some(c => c.imageUrl || (c.variations || []).some(v => v.imageUrl)) && (
            <>
              <button
                onClick={handleUploadAllCharactersToAssets}
                disabled={uploadingAllCharacterAssets}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold text-xs workshop-control-feedback disabled:opacity-60"
                style={{ background: `${theme.colors.accentPrimary}20`, color: theme.colors.accentPrimary, border: `1px solid ${theme.colors.accentPrimary}` }}
                title="把角色主体图和变体图加入资源中心并提交 GuaVideo 素材库"
              >
                {uploadingAllCharacterAssets ? <Loader2 size={13} className="animate-spin" /> : <PackagePlus size={13} />}
                {uploadingAllCharacterAssets ? "入库中" : "全部入库"}
              </button>
              {uploadingAllCharacterAssets && (
                <button
                  onClick={handleStopAllCharactersUpload}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold text-xs workshop-control-feedback"
                  style={{ background: `${theme.colors.error}20`, color: theme.colors.error, border: `1px solid ${theme.colors.error}` }}
                  title="停止当前批量入库提交"
                >
                  <X size={13} />
                  停止
                </button>
              )}
            </>
          )}
          {workspaceMode === 'assets' && (
            <button
              onClick={() => { setIsCreating(true); setIsGenerating(false); setIsUploading(false); setNewCharName(''); setNewCharPrompt(''); setNewCharAliases(''); setNewCharacterLayoutMode(DEFAULT_CHARACTER_LAYOUT_MODE); setNewCharacterAspectRatio('9:16'); setReferenceImages([]); }}
              className="workshop-primary-action flex items-center gap-1.5 px-4 py-1.5 rounded-lg font-bold text-xs"
              style={{ background: 'var(--color-accent-primary)', color: 'white' }}
            >
              <Plus size={13} /> 新建角色
            </button>
          )}
        </div>
      </div>

      {workspaceMode === 'random_casting' ? (
        <RandomCastingWorkspace
          theme={theme}
          overseasProject={isOverseasVideoProvider(assetProvider)}
          imageQualityTier={imageQualityTier}
          onCreatePlan={handleCreateRandomCastingPlan}
          onGeneratePreview={handleGenerateRandomCharacterPreview}
          onAddToWorkshop={handleAddRandomCharacterToWorkshop}
          onViewCharacter={(character) => {
            setSelectedCharId(character?.id || selectedCharId);
            setWorkspaceMode('assets');
          }}
        />
      ) : (
      /* 主体区域：左右分栏 */
      <div className="workshop-body flex-1 flex min-h-0">

        {/* 左侧：角色列表 */}
        <div className="workshop-sidebar w-56 border-r overflow-y-auto custom-scrollbar p-3 space-y-2" style={{ backgroundColor: 'var(--color-bg-secondary)', borderColor: 'var(--color-border-primary)' }}>
          {characters.map(char => (
            <div
              key={char.id}
              onClick={() => setSelectedCharId(char.id)}
              className="workshop-asset-card group relative rounded-lg overflow-hidden cursor-pointer"
              data-selected={selectedCharId === char.id}
            >
              {/* 21:9 三视图 */}
              <div className="aspect-[21/9] overflow-hidden" style={{ backgroundColor: 'var(--color-bg-overlay)' }}>
                <img src={char.imageUrl} className="w-full h-full object-contain" alt={char.name} />
              </div>
              <div className="workshop-asset-caption p-2">
                <h3 className="text-xs font-bold truncate" style={{ color: 'var(--color-text-primary)' }}>{char.name}</h3>
                {char.variations && char.variations.length > 0 && (
                  <p className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>{char.variations.length} 个变体</p>
                )}
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); setRenameTarget(char); }}
                className="workshop-card-action absolute top-1.5 left-1.5 p-1 rounded-md"
                style={{ backgroundColor: 'var(--overlay-medium)', color: 'white' }}
                title="更名"
                aria-label={`更名 ${char.name || "角色"}`}
              >
                {renamingId === char.id ? <Loader2 size={11} className="animate-spin" /> : <Edit2 size={11} />}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setDeleteTargetId(char.id); }}
                className="workshop-card-action absolute top-1.5 right-1.5 p-1 rounded-md"
                style={{ backgroundColor: 'var(--overlay-medium)', color: 'white' }}
                aria-label={`删除角色 ${char.name || '未命名角色'}`}
              >
                <Trash2 size={11} />
              </button>
            </div>
          ))}
          {characters.length === 0 && (
            <div className="workshop-list-empty flex flex-col items-center justify-center py-16">
              <WorkshopArtwork kind="character" size={28} className="mb-2" />
              <p className="text-2xs text-center" style={{ color: 'var(--color-text-muted)' }}>暂无角色<br/>点击右上角新建</p>
            </div>
          )}
        </div>

        {/* 右侧：详情面板 */}
        <div className="workshop-detail flex-1 overflow-y-auto custom-scrollbar">
          {selectedChar ? (
            <div className="workshop-detail-content p-6 max-w-6xl mx-auto space-y-5">

              {/* 主体定妆照区 */}
              <div className="workshop-panel rounded-xl p-5" style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)' }}>
                <div className="workshop-section-heading flex items-center justify-between mb-4">
                  <h3 className="workshop-section-title text-sm font-bold" style={{ color: 'var(--color-text-primary)' }}>主体定妆照</h3>
                  <div className="workshop-section-actions flex items-center gap-2">
                    <input type="file" ref={replaceMainInputRef} className="hidden" accept="image/*" onChange={handleReplaceMainImage} />
                    <button
                      onClick={() => replaceMainInputRef.current?.click()}
                      disabled={isReplacingMain}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-md text-2xs font-medium workshop-control-feedback disabled:opacity-50"
                      style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)', border: '1px solid var(--color-border-primary)' }}
                    >
                      {isReplacingMain ? <Loader2 size={10} className="animate-spin" /> : <Upload size={10} />}
                      {isReplacingMain ? '上传中...' : '替换'}
                    </button>
                    <button
                      onClick={handleRegenerateMain}
                      disabled={isRegeneratingMain}
                      className="workshop-primary-action flex items-center gap-1 px-2.5 py-1 rounded-md text-2xs font-medium disabled:opacity-50"
                      style={{ backgroundColor: `${theme.colors.accentPrimary}20`, color: theme.colors.accentPrimary, border: `1px solid ${theme.colors.accentPrimary}` }}
                    >
                      {isRegeneratingMain ? <Loader2 size={10} className="animate-spin" /> : <Wand2 size={10} />}
                      重新生成
                    </button>
                    {selectedChar.imageUrl && (
                      <button
                        onClick={() => updateRefinePanel({ show: !showRefinePanel, prompt: "" })}
                        disabled={isRefiningMain}
                        className="workshop-secondary-action flex items-center gap-1 px-2.5 py-1 rounded-md text-2xs font-medium workshop-control-feedback disabled:opacity-50"
                        data-active={showRefinePanel} style={{
                          backgroundColor: showRefinePanel ? theme.colors.accentPrimary : `${theme.colors.accentPrimary}20`,
                          color: showRefinePanel ? 'white' : theme.colors.accentPrimary,
                          border: `1px solid ${theme.colors.accentPrimary}`
                        }}
                      >
                        {isRefiningMain ? <Loader2 size={10} className="animate-spin" /> : <Edit2 size={10} />}
                        微调
                      </button>
                    )}
                    <ModelArkImageLibraryStatus registry={modelArkRegistry} url={selectedChar.imageUrl} />
                    {assetIngestSupported && selectedChar.imageUrl && (() => {
                      const meta = getEffectiveAssetMeta(selectedChar, assetProvider);
                      const status = meta.status;
                      const isUploading = uploadingAssetIds.has(selectedChar.id);
                      const isProcessing = status === "Processing";
                      const isActive = status === "Active";
                      const isReady = isAssetReadyStatus(status);
                      const isFailed = status === "Failed";
                      const disabled = isUploading || isProcessing || isReady;
                      return (
                        <>
                          <button
                            onClick={handleUploadToAssets}
                            disabled={disabled}
                            className="flex items-center gap-1 px-2.5 py-1 rounded-md text-2xs font-medium workshop-control-feedback disabled:opacity-60"
                            style={{
                              backgroundColor: isReady ? `${theme.colors.success}20` : isFailed ? `${theme.colors.error}20` : `${theme.colors.accentPrimary}20`,
                              color: isReady ? theme.colors.success : isFailed ? theme.colors.error : theme.colors.accentPrimary,
                              border: `1px solid ${isReady ? theme.colors.success : isFailed ? theme.colors.error : theme.colors.accentPrimary}`,
                            }}
                            title={meta.assetId ? `${assetLibraryLabel(meta.provider)}：${meta.assetId}` : `主体图和所有变体加入资源中心并提交到${assetLibraryLabel(meta.provider)}`}
                          >
                            {isUploading || isProcessing ? <Loader2 size={10} className="animate-spin" /> : isReady ? <Check size={10} /> : <PackagePlus size={10} />}
                            {isUploading ? '入库中...' : assetStatusLabel(status, isFailed ? '重新入库' : '一键入库')}
                          </button>
                          {(isUploading || isProcessing) && (
                            <button
                              onClick={() => handleStopAssetUpload({ clearCharacterProcessing: true })}
                              className="flex items-center gap-1 px-2.5 py-1 rounded-md text-2xs font-medium workshop-control-feedback"
                              style={{
                                backgroundColor: `${theme.colors.error}20`,
                                color: theme.colors.error,
                                border: `1px solid ${theme.colors.error}`,
                              }}
                              title="停止本地入库轮询，清空处理中状态后允许重新入库"
                            >
                              <X size={10} />
                              停止
                            </button>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* 微调面板 */}
                <Exitable
                  open={showRefinePanel && !!selectedChar.imageUrl}
                  className="mb-4 p-4 rounded-lg space-y-3"
                  style={{ backgroundColor: 'var(--color-bg-primary)', border: `1px solid ${theme.colors.accentPrimary}` }}
                  enterClass="animate-in fade-in slide-in-from-top-2 duration-200"
                  exitClass="animate-out fade-out slide-out-to-top-2 duration-150"
                >
                    <label className="text-2xs font-medium block" style={{ color: 'var(--color-text-muted)' }}>修改描述（基于当前图微调）</label>
                    <textarea
                      ref={refinePromptTextareaRef}
                      className="w-full rounded-md px-3 py-2 text-xs outline-none resize-none"
                      style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)', lineHeight: '1.5' }}
                      rows={2}
                      value={refinePrompt}
                      onChange={e => {
                        updateRefinePanel({ prompt: e.target.value });
                        const textarea = e.target;
                        textarea.style.height = 'auto';
                        textarea.style.height = textarea.scrollHeight + 'px';
                      }}
                      onFocus={e => {
                        const textarea = e.target;
                        textarea.style.height = 'auto';
                        textarea.style.height = textarea.scrollHeight + 'px';
                      }}
                      onBlur={e => {
                        const textarea = e.target;
                        textarea.style.height = 'auto';
                        textarea.style.height = textarea.scrollHeight + 'px';
                      }}
                      placeholder="例如：换成红色头发、加一副眼镜、改成冬装..."
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => updateRefinePanel({ show: false, prompt: "" })}
                        className="px-3 py-1.5 rounded-md text-xs"
                        style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}
                      >取消</button>
                      <button
                        onClick={handleRefineMain}
                        disabled={isRefiningMain || !refinePrompt.trim()}
                        className="workshop-secondary-action px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5 disabled:opacity-50"
                        style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}
                      >
                        {isRefiningMain ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />}
                        {isRefiningMain ? "微调中..." : "开始微调"}
                      </button>
                    </div>
                </Exitable>

                <div className="space-y-4">
                  {/* 21:9 主体图 - 有图或无图两种状态 */}
                  {selectedChar.imageUrl ? (
                    <div className="workshop-preview relative group aspect-[21/9] rounded-lg overflow-hidden cursor-zoom-in" aria-busy={isRegeneratingMain || isRefiningMain} style={{ backgroundColor: 'var(--color-bg-overlay)' }} onClick={() => setPreviewImage(selectedChar.imageUrl)}>
                      <img src={selectedChar.imageUrl} className="w-full h-full object-contain" alt={selectedChar.name} />
                      {(isRegeneratingMain || isRefiningMain) && (
                        <div className="workshop-preview-status" role="status">
                          <span className="workshop-preview-status-dot" aria-hidden="true" />
                          <span>{isRefiningMain ? '微调中' : '重新生成中'} · 显示上一版</span>
                        </div>
                      )}
                        <>
                          {/* 下载按钮 */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const link = document.createElement('a');
                              link.href = selectedChar.imageUrl;
                              link.download = `${selectedChar.name || '角色'}.png`;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            }}
                            className="workshop-preview-action absolute top-2 right-2 z-10 w-8 h-8 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: 'var(--overlay-medium)' }}
                            title="下载图片"
                          >
                            <Download size={16} className="text-white" />
                          </button>
                        </>
                    </div>
                  ) : (
                    /* 从大纲导入的角色，没有主体图，显示生成入口 */
                    <div className="aspect-[21/9] rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-3" style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-overlay)' }}>
                      <Sparkles size={32} style={{ color: 'var(--color-text-muted)', opacity: 0.4 }} />
                      <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>尚未生成定妆照</p>
                      <div className="flex gap-2">
                        <button
                          onClick={handleRegenerateMain}
                          disabled={isRegeneratingMain || !selectedChar.description}
                          className="workshop-primary-action flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold disabled:opacity-50"
                          style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}
                        >
                          {isRegeneratingMain ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
                          AI 生成
                        </button>
                        <button
                          onClick={() => replaceMainInputRef.current?.click()}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold"
                          style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border-primary)' }}
                        >
                          <Upload size={12} /> 上传图片
                        </button>
                      </div>
                      {!selectedChar.description && (
                        <p className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>（需要先填写角色描述才能 AI 生成）</p>
                      )}
                    </div>
                  )}

                  {/* 基本信息 */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-2xs font-medium mb-1.5 block" style={{ color: 'var(--color-text-muted)' }}>角色名称</label>
                      <div className="text-base font-bold" style={{ color: 'var(--color-text-primary)' }}>{selectedChar.name}</div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="text-2xs font-medium" style={{ color: 'var(--color-text-muted)' }}>别名（智能匹配）</label>
                        {!editingAlias && (
                          <button onClick={() => { setEditingAlias(true); setEditAliasText(charAliases.join(', ')); }} className="workshop-secondary-action workshop-link-action text-2xs flex items-center gap-1" style={{ color: theme.colors.accentPrimary }}>
                            <Edit2 size={9} /> 编辑
                          </button>
                        )}
                      </div>
                      {editingAlias ? (
                        <div className="flex gap-1.5">
                          <input
                            className="flex-1 rounded-md px-2 py-1.5 text-xs outline-none"
                            style={{ backgroundColor: 'var(--color-bg-primary)', border: `1px solid ${theme.colors.accentPrimary}`, color: 'var(--color-text-primary)' }}
                            value={editAliasText}
                            onChange={e => setEditAliasText(e.target.value)}
                            placeholder="小秦, 阿珂, 她..."
                          />
                          <button onClick={handleSaveAlias} className="workshop-secondary-action p-1.5 rounded-md" style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}><Check size={12} /></button>
                          <button onClick={() => setEditingAlias(false)} className="p-1.5 rounded-md" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}><X size={12} /></button>
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {charAliases.length > 0
                            ? charAliases.map((a, i) => <span key={i} className="text-2xs px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)' }}>{a}</span>)
                            : <span className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>暂无别名</span>
                          }
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-2xs font-medium" style={{ color: 'var(--color-text-muted)' }}>原始描述（AI 生成提示词）</label>
                      <div className="flex items-center gap-2">
                        {!editingDescription && (
                          <>
                            <button
                              onClick={handleExpandDescription}
                              disabled={isExpandingDescription || !(selectedChar.description || "").trim()}
                              className="workshop-secondary-action workshop-link-action text-2xs flex items-center gap-1 disabled:opacity-50"
                              style={{ color: theme.colors.accentPrimary }}
                              title="把性格/粗略描述扩写成五官体态表和生成提示词"
                            >
                              {isExpandingDescription ? <Loader2 size={9} className="animate-spin" /> : <Sparkles size={9} />}
                              扩写
                            </button>
                            <button onClick={() => { setEditingDescription(true); setEditDescriptionText(selectedChar.description || ""); }} className="workshop-secondary-action workshop-link-action text-2xs flex items-center gap-1" style={{ color: theme.colors.accentPrimary }}>
                              <Edit2 size={9} /> 编辑
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    {editingDescription ? (
                      <div className="space-y-2">
                        <textarea
                          rows={4}
                          className="w-full rounded-md px-3 py-2 text-xs outline-none resize-none"
                          style={{ backgroundColor: 'var(--color-bg-primary)', border: `1px solid ${theme.colors.accentPrimary}`, color: 'var(--color-text-primary)' }}
                          value={editDescriptionText}
                          onChange={e => {
                            setEditDescriptionText(e.target.value);
                            e.target.style.height = 'auto';
                            e.target.style.height = e.target.scrollHeight + 'px';
                          }}
                          onFocus={e => {
                            e.target.style.height = 'auto';
                            e.target.style.height = e.target.scrollHeight + 'px';
                          }}
                          placeholder="描述角色外貌、服装、发型..."
                        />
                        <div className="flex justify-end gap-1.5">
                          <button
                            onClick={handleExpandDescription}
                            disabled={isExpandingDescription || !editDescriptionText.trim()}
                            className="workshop-secondary-action px-2.5 py-1.5 rounded-md text-2xs font-medium flex items-center gap-1 disabled:opacity-50"
                            style={{ backgroundColor: `${theme.colors.accentPrimary}20`, color: theme.colors.accentPrimary, border: `1px solid ${theme.colors.accentPrimary}` }}
                          >
                            {isExpandingDescription ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />}
                            {isExpandingDescription ? "扩写中..." : "按五官体态扩写"}
                          </button>
                          <button onClick={handleSaveDescription} className="workshop-secondary-action p-1.5 rounded-md" style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}><Check size={12} /></button>
                          <button onClick={() => setEditingDescription(false)} className="p-1.5 rounded-md" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}><X size={12} /></button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-xs p-3 rounded-lg leading-relaxed whitespace-pre-wrap break-words" style={{ backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-secondary)' }}>
                        {selectedChar.description || "无描述"}
                      </div>
                    )}
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span className="text-2xs font-medium" style={{ color: 'var(--color-text-muted)' }}>生成版式</span>
                      <CharacterLayoutModeSelector
                        value={selectedCharacterLayoutMode}
                        onChange={setSelectedCharacterLayoutMode}
                        theme={theme}
                        compact
                        disabled={isRegeneratingMain}
                      />
                    </div>
                    {supportsCharacterAspectRatio(selectedCharacterLayoutMode) && (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <span className="text-2xs font-medium" style={{ color: 'var(--color-text-muted)' }}>图片比例</span>
                        <CharacterAspectRatioSelector
                          imageProvider={imageProvider}
                          value={selectedCharacterAspectRatio}
                          onChange={setSelectedCharacterAspectRatio}
                          theme={theme}
                          compact
                          disabled={isRegeneratingMain}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 参考图展示区 */}
              <div className="workshop-panel rounded-xl p-5" style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)' }}>
                <div className="workshop-section-heading flex items-center justify-between mb-3">
                  <h3 className="workshop-section-title text-sm font-bold" style={{ color: 'var(--color-text-primary)' }}>参考图</h3>
                  {!editingDetailRefs && (
                    <button
                      onClick={handleStartEditDetailRefs}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-md text-2xs font-medium workshop-control-feedback"
                      style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)', border: '1px solid var(--color-border-primary)' }}
                    >
                      <Edit2 size={10} /> 编辑
                    </button>
                  )}
                </div>

                {editingDetailRefs ? (
                  <div className="space-y-3">
                    <div className="space-y-2">
                      {detailRefImages.map((ref, idx) => (
                        <div key={idx} className="p-3 rounded-lg" style={{ backgroundColor: 'var(--color-bg-primary)', border: '1px solid var(--color-border-primary)' }}>
                          <div className="flex gap-3">
                            <div className="relative w-20 h-20 rounded-lg overflow-hidden shrink-0 cursor-zoom-in group" style={{ backgroundColor: 'var(--color-bg-overlay)' }} onClick={() => setPreviewImage(ref.url)}>
                              <img src={ref.url} className="w-full h-full object-cover" alt={`参考图${idx + 1}`} />
                              <button
                                onClick={(e) => { e.stopPropagation(); handleRemoveDetailRefImage(idx); }}
                                className="absolute top-1 right-1 p-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                style={{ backgroundColor: 'var(--overlay-medium)', color: 'white' }}
                              >
                                <X size={10} />
                              </button>
                            </div>
                            <div className="flex-1 space-y-1.5">
                              <div className="text-2xs font-medium" style={{ color: 'var(--color-text-muted)' }}>参考图{idx + 1} - 用途说明</div>
                              <input
                                type="text"
                                className="w-full rounded-md px-2 py-1.5 text-xs outline-none"
                                style={{ backgroundColor: 'transparent', color: 'var(--color-text-primary)' }}
                                placeholder="例如：参考这套战斗服的设计、参考眼妆和唇色"
                                value={ref.label}
                                onChange={(e) => handleUpdateDetailRefLabel(idx, e.target.value)}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                      {detailRefImages.length < maxReferenceImages && (
                        <>
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*"
                            ref={el => detailRefInputRefs.current[0] = el}
                            onChange={handleUploadDetailRefImage}
                          />
                          <button
                            onClick={() => detailRefInputRefs.current[0]?.click()}
                            disabled={uploadingDetailRefIndex !== null}
                            className="w-full py-2 rounded-lg border-2 border-dashed flex items-center justify-center gap-2 workshop-control-feedback hover:border-solid disabled:opacity-50"
                            style={{ borderColor: 'var(--color-border-primary)', color: 'var(--color-text-muted)' }}
                          >
                            {uploadingDetailRefIndex !== null ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <>
                                <Plus size={14} />
                                <span className="text-xs">添加参考图</span>
                              </>
                            )}
                          </button>
                        </>
                      )}
                    </div>
                    <p className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>
                      用途说明会告诉 AI 参考图的哪个部分，输出仍为标准角色定妆照（左4脸+右3视图）
                    </p>
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setEditingDetailRefs(false)}
                        className="px-3 py-1.5 rounded-md text-xs"
                        style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}
                      >
                        取消
                      </button>
                      <button
                        onClick={handleSaveDetailRefs}
                        className="workshop-secondary-action px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5"
                        style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}
                      >
                        <Check size={11} /> 保存
                      </button>
                    </div>
                  </div>
                ) : (
                  (() => {
                    const savedRefs = (() => { try { const r = JSON.parse(selectedChar.referenceImages || '[]'); return r.map(x => typeof x === 'string' ? { url: x, label: '' } : x); } catch { return []; } })();
                    if (savedRefs.length === 0) {
                      return (
                        <div className="flex flex-col items-center justify-center py-8">
                          <ImagePlus size={24} className="mb-2 opacity-20" style={{ color: 'var(--color-text-muted)' }} />
                          <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>暂无参考图，点击"编辑"添加</p>
                        </div>
                      );
                    }
                    return (
                      <div className="space-y-2">
                        {savedRefs.map((ref, idx) => (
                          <div key={idx} className="p-3 rounded-lg" style={{ backgroundColor: 'var(--color-bg-primary)', border: '1px solid var(--color-border-primary)' }}>
                            <div className="flex gap-3">
                              <div className="relative w-20 h-20 rounded-lg overflow-hidden shrink-0 cursor-zoom-in" style={{ backgroundColor: 'var(--color-bg-overlay)' }} onClick={() => setPreviewImage(ref.url)}>
                                <img src={ref.url} className="w-full h-full object-cover" alt={`参考图${idx + 1}`} />
                              </div>
                              <div className="flex-1">
                                <div className="text-2xs font-medium mb-1" style={{ color: 'var(--color-text-muted)' }}>参考图{idx + 1}</div>
                                <div className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{ref.label || "无用途说明"}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()
                )}
              </div>

              {/* 角色参考图变体区 */}
              <div className="workshop-panel rounded-xl p-5" style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)' }}>
                <div className="workshop-section-heading flex items-center justify-between mb-4">
                  <h3 className="workshop-section-title text-sm font-bold" style={{ color: 'var(--color-text-primary)' }}>角色参考图变体</h3>
                  <div className="flex flex-wrap items-center justify-end gap-2">
                  <WorkshopVariantUpload
                    key={`${projectId}-${selectedChar.id}`}
                    assetId={selectedChar.id}
                    assetName={selectedChar.name}
                    assetLabel="角色"
                    disabled={isGeneratingVariant}
                    onUpload={handleUploadNewVariant}
                  />
                  <button
                    onClick={() => updateVariantPanel({ show: !showVariantPanel })}
                    className="workshop-secondary-action flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold text-xs workshop-control-feedback"
                    data-active={showVariantPanel} style={{ backgroundColor: showVariantPanel ? 'var(--color-bg-tertiary)' : theme.colors.accentPrimary, color: showVariantPanel ? 'var(--color-text-muted)' : 'white' }}
                  >
                    {showVariantPanel ? <X size={11} /> : <Plus size={11} />}
                    {showVariantPanel ? '取消' : '生成变体'}
                  </button>
                  </div>
                </div>

                {/* 变体生成面板 */}
                <Exitable
                  open={showVariantPanel}
                  className="mb-4 p-4 rounded-lg space-y-3"
                  style={{ backgroundColor: 'var(--color-bg-primary)', border: `1px solid ${theme.colors.accentPrimary}` }}
                  enterClass="animate-in fade-in slide-in-from-top-2 duration-200"
                  exitClass="animate-out fade-out slide-out-to-top-2 duration-150"
                >
                    <div>
                      <label className="text-2xs font-medium mb-1.5 block" style={{ color: 'var(--color-text-muted)' }}>变体名称</label>
                      <input
                        className="w-full rounded-md px-3 py-2 text-xs outline-none"
                        style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                        value={variantName}
                        onChange={e => updateVariantPanel({ name: e.target.value })}
                        placeholder="例如：战斗服"
                      />
                    </div>

                    {/* 模式切换 */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => updateVariantPanel({ mode: "text" })}
                        className="workshop-secondary-action flex-1 py-2 rounded-md text-xs font-bold workshop-control-feedback"
                        data-active={variantMode === "text"} style={{ backgroundColor: variantMode === "text" ? theme.colors.accentPrimary : 'var(--color-bg-tertiary)', color: variantMode === "text" ? 'white' : 'var(--color-text-muted)' }}
                      >
                        文字换装
                      </button>
                      <button
                        onClick={() => updateVariantPanel({ mode: "image" })}
                        className="workshop-secondary-action flex-1 py-2 rounded-md text-xs font-bold workshop-control-feedback"
                        data-active={variantMode === "image"} style={{ backgroundColor: variantMode === "image" ? theme.colors.accentPrimary : 'var(--color-bg-tertiary)', color: variantMode === "image" ? 'white' : 'var(--color-text-muted)' }}
                      >
                        参考图换装
                      </button>
                    </div>

                    {variantMode === "text" ? (
                      <div>
                        <label className="text-2xs font-medium mb-1.5 block" style={{ color: 'var(--color-text-muted)' }}>换装描述（基于主体生成）</label>
                        <textarea
                          ref={variantPromptTextareaRef}
                          rows={2}
                          className="w-full rounded-md px-3 py-2 text-xs outline-none resize-none"
                          style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)', lineHeight: '1.5' }}
                          value={variantPrompt}
                          onChange={e => {
                            updateVariantPanel({ prompt: e.target.value });
                            const textarea = e.target;
                            textarea.style.height = 'auto';
                            textarea.style.height = textarea.scrollHeight + 'px';
                          }}
                          onFocus={e => {
                            const textarea = e.target;
                            textarea.style.height = 'auto';
                            textarea.style.height = textarea.scrollHeight + 'px';
                          }}
                          onBlur={e => {
                            const textarea = e.target;
                            textarea.style.height = 'auto';
                            textarea.style.height = textarea.scrollHeight + 'px';
                          }}
                          placeholder="例如：穿着黑色战斗服，手持武器"
                        />
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div>
                          <label className="text-2xs font-medium mb-1.5 block" style={{ color: 'var(--color-text-muted)' }}>上传参考图</label>
                          <input type="file" ref={variantRefInputRef} className="hidden" accept="image/*" onChange={handleUploadVariantRef} />
                          {variantRefImage ? (
                            <div className="flex gap-3 p-3 rounded-lg" style={{ backgroundColor: 'var(--color-bg-primary)', border: '1px solid var(--color-border-primary)' }}>
                              <div className="relative w-20 h-20 rounded-lg overflow-hidden shrink-0 group" style={{ backgroundColor: 'var(--color-bg-overlay)' }}>
                                <img src={variantRefImage} className="w-full h-full object-cover" alt="参考图" />
                                <button onClick={() => updateVariantPanel({ refImage: null, refLabel: "" })} className="absolute top-1 right-1 p-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" style={{ backgroundColor: 'var(--overlay-medium)', color: 'white' }}>
                                  <X size={10} />
                                </button>
                              </div>
                              <div className="flex-1 space-y-1.5">
                                <div className="text-2xs font-medium" style={{ color: 'var(--color-text-muted)' }}>用途说明</div>
                                <input
                                  type="text"
                                  className="w-full rounded-md px-2 py-1.5 text-xs outline-none"
                                  style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                                  placeholder="例如：参考这套战斗服的设计、参考裙子的款式"
                                  value={variantRefLabel}
                                  onChange={(e) => updateVariantPanel({ refLabel: e.target.value })}
                                />
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => variantRefInputRef.current?.click()}
                              className="w-full py-6 rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-2 workshop-control-feedback hover:border-solid"
                              style={{ borderColor: 'var(--color-border-primary)', color: 'var(--color-text-muted)' }}
                            >
                              <ImagePlus size={24} />
                              <span className="text-xs">点击上传参考图</span>
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    <p className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>
                      {getCharacterVariantLayoutHint(selectedCharacterLayoutMode, selectedCharacterAspectRatio)}
                    </p>

                    <div className="flex justify-end gap-2 pt-2">
                      <button
                        onClick={() => updateVariantPanel({ show: false, name: "", prompt: "", refImage: null })}
                        className="px-3 py-1.5 rounded-md text-xs"
                        style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}
                      >
                        取消
                      </button>
                      <button
                        onClick={handleGenerateVariant}
                        disabled={isGeneratingVariant || !variantName.trim() || (variantMode === "text" && !variantPrompt.trim()) || (variantMode === "image" && !variantRefImage)}
                        className="workshop-primary-action px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5 disabled:opacity-50"
                        style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}
                      >
                        {isGeneratingVariant ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />}
                        {isGeneratingVariant ? "生成中..." : "生成变体"}
                      </button>
                    </div>
                </Exitable>

                {/* 变体列表（手风琴） */}
                <div className="space-y-2">
                  {visibleVariantItems.map(({ variation: v, index: vi }) => {
                    const isExpanded = expandedVariantIndex === vi;
                    const isRegenerating = getRegeneratingVariantIndex(vi);
                    const vMeta = getEffectiveAssetMeta(v, assetProvider);
                    const vStatus = vMeta.status;
                    const isVUploading = uploadingAssetIds.has(`${selectedChar.id}-v${vi}`);
                    const isVProcessing = vStatus === "Processing";
	                    const isVActive = vStatus === "Active";
	                    const isVReady = isAssetReadyStatus(vStatus);
	                    const isVFailed = vStatus === "Failed";

                    return (
                      <div key={vi} data-expanded={isExpanded} className="workshop-variant-card rounded-lg overflow-hidden" style={{ border: `1px solid ${isExpanded ? theme.colors.accentPrimary : 'var(--color-border-primary)'}`, backgroundColor: 'var(--color-bg-primary)' }}>
                        {/* 折叠头 */}
                        <div
                          className="workshop-variant-heading flex items-center gap-3 px-3 py-2 cursor-pointer workshop-control-feedback"
                          style={{ backgroundColor: isExpanded ? `${theme.colors.accentPrimary}10` : 'transparent' }}
                          onClick={() => {
                            setExpandedVariantIndex(isExpanded ? null : vi);
                            updateVariantRefinePanel({ show: false, prompt: "" });
                            setEditingVariantDesc(false);
                          }}
                        >
                          <ChevronRight
                            size={14}
                            className="ui-accordion-chevron"
                            data-open={isExpanded}
                            style={{ color: isExpanded ? theme.colors.accentPrimary : 'var(--color-text-muted)' }}
                          />
                          <div className="w-16 h-7 rounded overflow-hidden shrink-0" style={{ backgroundColor: 'var(--color-bg-overlay)' }}>
                            {v.imageUrl && <img src={v.imageUrl} className="w-full h-full object-cover" alt={v.title} />}
                          </div>
                          <span className="text-xs font-bold flex-1 truncate" style={{ color: 'var(--color-text-primary)' }}>{v.title}</span>
                          {isRegenerating && <Loader2 size={12} className="animate-spin" style={{ color: theme.colors.bluePrimary }} />}
                          <button
                            onClick={(e) => { e.stopPropagation(); setDeleteVariantIndex(vi); }}
                            className="workshop-variant-delete p-1 rounded-md workshop-control-feedback"
                            style={{ color: 'var(--color-text-muted)' }}
                          >
                            <Trash2 size={11} />
                          </button>
                        </div>

                        {/* 展开内容 */}
                        <div className="ui-accordion-grid" data-open={isExpanded}>
                          <div className="ui-accordion-content" inert={!isExpanded} aria-hidden={!isExpanded}>
                            <div className="px-4 pb-4 space-y-3" style={{ borderTop: '1px solid var(--color-border-primary)' }}>
                            {/* 操作按钮行 */}
                            <div className="flex flex-wrap items-center gap-2 pt-3">
                              <input
                                type="file"
                                ref={(node) => {
                                  if (node) variantReplaceInputRefs.current.set(vi, node);
                                  else variantReplaceInputRefs.current.delete(vi);
                                }}
                                className="hidden"
                                accept="image/*"
                                onChange={(e) => handleReplaceVariantImage(e, vi)}
                              />
                              <button
                                onClick={() => variantReplaceInputRefs.current.get(vi)?.click()}
                                disabled={isRegenerating}
                                className="flex items-center gap-1 px-2.5 py-1 rounded-md text-2xs font-medium workshop-control-feedback disabled:opacity-50"
                                style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)', border: '1px solid var(--color-border-primary)' }}
                              >
                                <Upload size={10} /> {v.imageUrl ? '替换图片' : '上传图片'}
                              </button>
                              <button
                                onClick={() => handleRegenerateVariant(vi)}
                                disabled={isRegenerating}
                                className="workshop-secondary-action flex items-center gap-1 px-2.5 py-1 rounded-md text-2xs font-medium workshop-control-feedback disabled:opacity-50"
                                style={{ backgroundColor: `${theme.colors.accentPrimary}20`, color: theme.colors.accentPrimary, border: `1px solid ${theme.colors.accentPrimary}` }}
                              >
                                {isRegenerating ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
                                重新生成
                              </button>
                              {v.imageUrl && (
                                <button
                                  onClick={() => updateVariantRefinePanel({ show: !showVariantRefinePanel, prompt: "" })}
                                  disabled={isRegenerating}
                                  className="workshop-secondary-action flex items-center gap-1 px-2.5 py-1 rounded-md text-2xs font-medium workshop-control-feedback disabled:opacity-50"
                                  data-active={showVariantRefinePanel} style={{
                                    backgroundColor: showVariantRefinePanel ? theme.colors.accentPrimary : `${theme.colors.accentPrimary}20`,
                                    color: showVariantRefinePanel ? 'white' : theme.colors.accentPrimary,
                                    border: `1px solid ${theme.colors.accentPrimary}`
                                  }}
                                >
                                  <Edit2 size={10} /> 微调
                                </button>
                              )}
                              <ModelArkImageLibraryStatus registry={modelArkRegistry} url={v.imageUrl} />
                              {assetIngestSupported && v.imageUrl && (() => {
                                const disabled = isVUploading || isVProcessing || isVReady;
                                return (
                                  <>
                                    <button
                                      onClick={() => handleUploadVariantToAssets(vi)}
                                      disabled={disabled}
                                      className="flex items-center gap-1 px-2.5 py-1 rounded-md text-2xs font-medium workshop-control-feedback disabled:opacity-60"
                                      style={{
                                        backgroundColor: isVReady ? `${theme.colors.success}20` : isVFailed ? `${theme.colors.error}20` : `${theme.colors.accentPrimary}20`,
                                        color: isVReady ? theme.colors.success : isVFailed ? theme.colors.error : theme.colors.accentPrimary,
                                        border: `1px solid ${isVReady ? theme.colors.success : isVFailed ? theme.colors.error : theme.colors.accentPrimary}`,
                                      }}
                                    >
                                      {isVUploading || isVProcessing ? <Loader2 size={10} className="animate-spin" /> : isVReady ? <Check size={10} /> : <PackagePlus size={10} />}
                                      {isVUploading ? '入库中...' : assetStatusLabel(vStatus, isVFailed ? '入库失败' : '素材入库')}
                                    </button>
                                    {(isVUploading || isVProcessing) && (
                                      <button
                                        onClick={() => handleStopAssetUpload({ variantIndex: vi })}
                                        className="flex items-center gap-1 px-2.5 py-1 rounded-md text-2xs font-medium workshop-control-feedback"
                                        style={{
                                          backgroundColor: `${theme.colors.error}20`,
                                          color: theme.colors.error,
                                          border: `1px solid ${theme.colors.error}`,
                                        }}
                                        title="停止本地入库轮询，清空处理中状态后允许重新入库"
                                      >
                                        <X size={10} />
                                        停止
                                      </button>
                                    )}
                                  </>
                                );
                              })()}
                            </div>

                            {/* 变体微调面板 */}
                            <Exitable
                              open={isExpanded && showVariantRefinePanel && !!v.imageUrl}
                              className="p-3 rounded-lg space-y-2"
                              style={{ backgroundColor: 'var(--color-bg-secondary)', border: `1px solid ${theme.colors.accentPrimary}` }}
                              enterClass="animate-in fade-in slide-in-from-top-2 duration-200"
                              exitClass="animate-out fade-out slide-out-to-top-2 duration-150"
                            >
                                <label className="text-2xs font-medium block" style={{ color: 'var(--color-text-muted)' }}>修改描述（基于当前变体图微调）</label>
                                <textarea
                                  ref={isExpanded ? variantRefinePromptTextareaRef : null}
                                  className="w-full rounded-md px-3 py-2 text-xs outline-none resize-none"
                                  style={{ backgroundColor: 'var(--color-bg-primary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)', lineHeight: '1.5' }}
                                  rows={2}
                                  value={variantRefinePrompt}
                                  onChange={e => {
                                    updateVariantRefinePanel({ prompt: e.target.value });
                                    const textarea = e.target;
                                    textarea.style.height = 'auto';
                                    textarea.style.height = textarea.scrollHeight + 'px';
                                  }}
                                  onFocus={e => {
                                    const textarea = e.target;
                                    textarea.style.height = 'auto';
                                    textarea.style.height = textarea.scrollHeight + 'px';
                                  }}
                                  onBlur={e => {
                                    const textarea = e.target;
                                    textarea.style.height = 'auto';
                                    textarea.style.height = textarea.scrollHeight + 'px';
                                  }}
                                  placeholder="例如：换成深色版本、加上披风..."
                                />
                                <div className="flex justify-end gap-2">
                                  <button onClick={() => updateVariantRefinePanel({ show: false, prompt: "" })} className="px-3 py-1.5 rounded-md text-xs" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}>取消</button>
                                  <button
                                    onClick={() => handleRefineVariant(vi)}
                                    disabled={isRegenerating || !variantRefinePrompt.trim()}
                                    className="workshop-secondary-action px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5 disabled:opacity-50"
                                    style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}
                                  >
                                    {isRegenerating ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />}
                                    {isRegenerating ? "微调中..." : "开始微调"}
                                  </button>
                                </div>
                            </Exitable>

                            {/* 大图 */}
                            {v.imageUrl ? (
                              <div className="workshop-preview relative group aspect-[21/9] rounded-lg overflow-hidden cursor-zoom-in" aria-busy={isRegenerating} style={{ backgroundColor: 'var(--color-bg-overlay)' }} onClick={() => setPreviewImage(v.imageUrl)}>
                                <img src={v.imageUrl} className="w-full h-full object-contain" alt={v.title} />
                                {isRegenerating && (
                                  <div className="workshop-preview-status" role="status">
                                    <span className="workshop-preview-status-dot" aria-hidden="true" />
                                    <span>生成中 · 显示上一版</span>
                                  </div>
                                )}
                                  <>
                                    {/* 下载按钮 */}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const link = document.createElement('a');
                                        link.href = v.imageUrl;
                                        link.download = `${selectedChar?.name || '角色'}-${v.title || '变体'}.png`;
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                      }}
                                      className="workshop-preview-action absolute top-2 right-2 z-10 w-7 h-7 rounded-full flex items-center justify-center"
                                      style={{ backgroundColor: 'var(--overlay-medium)' }}
                                      title="下载图片"
                                    >
                                      <Download size={14} className="text-white" />
                                    </button>
                                  </>
                              </div>
                            ) : (
                              <div className="aspect-[21/9] rounded-lg border-2 border-dashed flex items-center justify-center" style={{ borderColor: 'var(--color-border-primary)' }}>
                                <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>暂无图片</p>
                              </div>
                            )}

                            {/* 变体描述 */}
                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <label className="text-2xs font-medium" style={{ color: 'var(--color-text-muted)' }}>变体描述</label>
                                {!editingVariantDesc && (
                                  <button onClick={() => { setEditingVariantDesc(true); setEditVariantDescText(v.description || ""); }} className="workshop-secondary-action workshop-link-action text-2xs flex items-center gap-1" style={{ color: theme.colors.accentPrimary }}>
                                    <Edit2 size={9} /> 编辑
                                  </button>
                                )}
                              </div>
                              {editingVariantDesc ? (
                                <div className="space-y-2">
                                  <textarea
                                    className="w-full rounded-md px-3 py-2 text-xs outline-none resize-none"
                                    style={{ backgroundColor: 'var(--color-bg-secondary)', border: `1px solid ${theme.colors.accentPrimary}`, color: 'var(--color-text-primary)' }}
                                    value={editVariantDescText}
                                    onChange={e => setEditVariantDescText(e.target.value)}
                                    placeholder="描述这个变体的服装/状态..."
                                    rows={10}
                                  />
                                  <div className="flex justify-end gap-1.5">
                                    <button onClick={() => handleSaveVariantDescription(vi)} className="workshop-secondary-action p-1.5 rounded-md" style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}><Check size={12} /></button>
                                    <button onClick={() => setEditingVariantDesc(false)} className="p-1.5 rounded-md" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}><X size={12} /></button>
                                  </div>
                                </div>
                              ) : (
                                <div className="text-xs p-2 rounded-md leading-relaxed" style={{ backgroundColor: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)' }}>
                                  {v.description || "无描述"}
                                </div>
                              )}
                            </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {visibleVariantItems.length === 0 && (
                    <div className="workshop-variant-empty flex flex-col items-center justify-center py-12">
                      <Sparkles size={24} className="mb-2 opacity-20" style={{ color: 'var(--color-text-muted)' }} />
                      <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>暂无变体，点击"生成变体"开始</p>
                    </div>
                  )}
                </div>
              </div>

            </div>
          ) : (
            <div className="workshop-empty flex flex-col items-center justify-center h-full">
              <WorkshopArtwork kind="character" size={48} className="mb-3" />
              <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>请从左侧选择角色</p>
            </div>
          )}
        </div>
      </div>
      )}

      {/* 新建角色弹窗 */}
      {isCreating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'var(--overlay-medium)', backdropFilter: 'blur(8px)' }}>
          <div className="rounded-xl p-8 max-w-5xl w-full max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in-95 duration-200" style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold" style={{ color: 'var(--color-text-primary)' }}>新建角色</h3>
              <button onClick={() => { setIsCreating(false); setNewCharName(""); setNewCharPrompt(""); setNewCharAliases(""); setNewCharacterLayoutMode(DEFAULT_CHARACTER_LAYOUT_MODE); setNewCharacterAspectRatio("9:16"); setReferenceImages([]); }} className="p-1 rounded-lg" style={{ color: 'var(--color-text-muted)' }}><X size={18} /></button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold mb-1.5 block" style={{ color: 'var(--color-text-muted)' }}>画风</label>
                  <AppSelect value={currentStyle} onChange={(e) => setCurrentStyle(e.target.value)} className="w-full rounded-lg px-3 py-2 text-xs outline-none" style={{ backgroundColor: 'var(--color-bg-primary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}>
                    {STYLE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </AppSelect>
                </div>
                <div>
                  <label className="text-xs font-bold mb-1.5 block" style={{ color: 'var(--color-text-muted)' }}>角色名称</label>
                  <input value={newCharName} onChange={(e) => setNewCharName(e.target.value)} className="w-full rounded-lg px-3 py-2 text-xs outline-none" style={{ backgroundColor: 'var(--color-bg-primary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }} placeholder="例如：秦珂" />
                </div>
              </div>

              <div>
                <label className="text-xs font-bold mb-1.5 block" style={{ color: 'var(--color-text-muted)' }}>别名（逗号分隔）</label>
                <input value={newCharAliases} onChange={(e) => setNewCharAliases(e.target.value)} className="w-full rounded-lg px-3 py-2 text-xs outline-none" style={{ backgroundColor: 'var(--color-bg-primary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }} placeholder="小秦, 阿珂, 她" />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-bold block" style={{ color: 'var(--color-text-muted)' }}>角色描述（用于 AI 生成三视图）</label>
                  <button
                    onClick={handleExpandNewPrompt}
                    disabled={isExpandingNewPrompt || !newCharPrompt.trim()}
                    className="workshop-secondary-action workshop-link-action text-2xs font-medium flex items-center gap-1 disabled:opacity-50"
                    style={{ color: theme.colors.accentPrimary }}
                    title="把性格/粗略描述扩写成五官体态表和生成提示词"
                  >
                    {isExpandingNewPrompt ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />}
                    {isExpandingNewPrompt ? "扩写中..." : "扩写提示词"}
                  </button>
                </div>
                <textarea rows={8} value={newCharPrompt} onChange={(e) => setNewCharPrompt(e.target.value)} className="w-full rounded-lg px-3 py-2 text-xs outline-none resize-none" style={{ backgroundColor: 'var(--color-bg-primary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }} placeholder="描述角色外貌、服装、发型...&#10;&#10;可引用参考图，例如：&#10;参考图1的脸型，参考图2的妆造，参考图3的服装" />
                <div className="mt-2">
                  <CharacterLayoutModeSelector
                    value={newCharacterLayoutMode}
                    onChange={setNewCharacterLayoutMode}
                    theme={theme}
                    disabled={isGenerating || isUploading}
                  />
                </div>
                {supportsCharacterAspectRatio(newCharacterLayoutMode) && (
                  <div className="mt-2">
                    <div className="mb-1 text-2xs font-medium" style={{ color: 'var(--color-text-muted)' }}>图片比例</div>
                    <CharacterAspectRatioSelector
                      imageProvider={imageProvider}
                      value={newCharacterAspectRatio}
                      onChange={setNewCharacterAspectRatio}
                      theme={theme}
                      disabled={isGenerating || isUploading}
                    />
                  </div>
                )}
                <p className="text-2xs mt-1.5" style={{ color: 'var(--color-text-muted)' }}>
                  可以先写性格或人设，例如“清冷、克制、外冷内热”，再点扩写转成可生成的五官体态描述。
                </p>
              </div>

              {/* 参考图上传区 */}
              <div>
                <label className="text-xs font-bold mb-1.5 block" style={{ color: 'var(--color-text-muted)' }}>
                  参考图（可选，最多{maxReferenceImages}张）
                </label>
                <div className="space-y-2">
                  {referenceImages.map((ref, idx) => (
                    <div key={idx} className="p-3 rounded-lg" style={{ backgroundColor: 'var(--color-bg-primary)', border: '1px solid var(--color-border-primary)' }}>
                      <div className="flex gap-3">
                        <div className="relative w-20 h-20 rounded-lg overflow-hidden shrink-0 cursor-zoom-in group" style={{ backgroundColor: 'var(--color-bg-overlay)' }} onClick={() => setPreviewImage(ref.url)}>
                          <img src={ref.url} className="w-full h-full object-cover" alt={`参考图${idx + 1}`} />
                          <button
                            onClick={(e) => { e.stopPropagation(); handleRemoveRefImage(idx); }}
                            className="absolute top-1 right-1 p-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            style={{ backgroundColor: 'var(--overlay-medium)', color: 'white' }}
                          >
                            <X size={10} />
                          </button>
                        </div>
                        <div className="flex-1 space-y-1.5">
                          <div className="text-2xs font-medium" style={{ color: 'var(--color-text-muted)' }}>参考图{idx + 1} - 用途说明</div>
                          <input
                            type="text"
                            className="w-full rounded-md px-2 py-1.5 text-xs outline-none"
                            style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                            placeholder="例如：参考这套战斗服的设计、参考眼妆和唇色"
                            value={ref.label}
                            onChange={(e) => handleUpdateRefLabel(idx, e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  {referenceImages.length < maxReferenceImages && (
                    <>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        ref={el => refImageInputRefs.current[0] = el}
                        onChange={handleUploadRefImage}
                      />
                      <button
                        onClick={() => refImageInputRefs.current[0]?.click()}
                        disabled={uploadingRefIndex !== null}
                        className="w-full py-2 rounded-lg border-2 border-dashed flex items-center justify-center gap-2 workshop-control-feedback hover:border-solid disabled:opacity-50"
                        style={{ borderColor: 'var(--color-border-primary)', color: 'var(--color-text-muted)' }}
                      >
                        {uploadingRefIndex !== null ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <>
                            <Plus size={14} />
                            <span className="text-xs">添加参考图</span>
                          </>
                        )}
                      </button>
                    </>
                  )}
                </div>
                <p className="text-2xs mt-2" style={{ color: 'var(--color-text-muted)' }}>
                  提示：用途说明会告诉 AI 参考图的哪个部分（服装/妆容/配饰等），输出仍为标准角色定妆照（左4脸+右3视图）
                </p>
              </div>

              {/* 操作按钮 */}
              <div className="grid grid-cols-2 gap-3">
                <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleUploadMain} />
                <button onClick={handleGenerateMain} disabled={isGenerating} className="workshop-primary-action py-2.5 rounded-lg font-bold flex items-center justify-center gap-2 text-xs disabled:opacity-50" style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}>
                  {isGenerating ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />}
                  {isGenerating ? "创建中..." : "AI 生成"}
                </button>
                <button onClick={() => fileInputRef.current?.click()} disabled={isUploading} className="py-2.5 rounded-lg font-bold flex items-center justify-center gap-2 text-xs" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border-primary)' }}>
                  {isUploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                  {isUploading ? "上传中..." : "上传图片"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AssetRenameDialog
        isOpen={!!renameTarget}
        assetLabel="角色"
        currentName={renameTarget?.name}
        saving={renamingId === renameTarget?.id}
        onClose={() => setRenameTarget(null)}
        onConfirm={handleRename}
      />

      {/* 删除角色确认 */}
      <ConfirmDialog
        isOpen={!!deleteTargetId}
        onClose={() => setDeleteTargetId(null)}
        onConfirm={handleDelete}
        title="移入回收站"
        message="确定将这个角色资产移入回收站吗？移入后将不再显示，可联系管理员恢复。"
        confirmLabel="移入回收站"
        variant="danger"
      />

      {/* 删除变体确认 */}
      <ConfirmDialog
        isOpen={deleteVariantIndex !== null}
        onClose={() => setDeleteVariantIndex(null)}
        onConfirm={() => handleDeleteVariant(deleteVariantIndex)}
        title={charVariations[deleteVariantIndex]?.imageUrl ? '移入回收站' : '移除变体草稿'}
        message={charVariations[deleteVariantIndex]?.imageUrl ? '确定将这个角色变体移入回收站吗？可联系管理员恢复。' : '这个变体尚未生成图片，移除后不会进入媒体回收站。确定移除草稿吗？'}
        confirmLabel={charVariations[deleteVariantIndex]?.imageUrl ? '移入回收站' : '移除草稿'}
        variant="danger"
      />
    </div>
  );
};

export default React.memo(CharacterGenerator);
